import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { resolveGatewayParams, buildRuijieHandoffUrl, normalizeGuestMac, hasGatewayParams } from './gatewayParams';
import { gatewayLogoutUrl, saveGuestSession, loadGuestSession, markGuestDisconnected, clearGuestSession, summaryRows } from './guestSession';
import { isAppleDevice, reloadForOnlineCheck } from './appleDevice';
import { pageLookOf } from './landingPageStyles';
import { portalAPI } from '../../services/api';
import axios from 'axios';
import { toast } from 'react-toastify';
import './Landing.css';
import { AUTH_METHOD_REGISTRY } from './authRegistry';
import VoucherLoginFields from './loginMethods/VoucherLoginFields';
import PMSLoginFields from './loginMethods/PMSLoginFields';
import OTPLoginFields from './loginMethods/OTPLoginFields';
import ResidentLoginFields from './loginMethods/ResidentLoginFields';
import StaffLoginFields from './loginMethods/StaffLoginFields';
import LandingPageRenderer, { hasStatusPanel, StatusScreen } from './LandingPageRenderer';
import TermsModal from './TermsModal';

// Bundled assets for the "Oakwood ASR" built-in theme preset (selected via
// Templates → Theme Preset). Fixed images shipped with the app — not
// admin-uploadable — used only when template.themePreset === 'oakwood-asr'.
import OAKWOOD_HERO from './assets/hero-oakwood.jpg';
import OAKWOOD_HEADER from './assets/login-brand-header.png';
import OAKWOOD_BRANDS from './assets/brand-family.png';
import { formatTime } from '../../utils/dateFormat';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
// The portal's credit line (every classic page's footer).
const PORTAL_CREDIT = 'Powered by HotWiFi · Developed by AsyncPulse Technologies';
// How long a builder page shows "You're connected" before the gateway takes the guest on.
const CONNECTED_SCREEN_MS = 4000;

// "Oakwood ASR" built-in theme preset CSS — self-contained, scoped under
// .asr-root so it never leaks into / clashes with the normal portal CSS.
const OAKWOOD_ASR_CSS = `
.asr-root {
  --teal: #10505a;
  --teal-90: rgba(16, 80, 90, 0.90);
  --gold: #c4a05f;
  --gold-soft: #d9c08a;

  font-family: "Helvetica Neue", Helvetica, Arial, "Segoe UI", sans-serif;
  color: #222;
  background: #fff;
  line-height: 1.5;
  min-height: 100vh;
}

.asr-root *,
.asr-root *::before,
.asr-root *::after {
  box-sizing: border-box;
}

.asr-top {
  position: relative;
  overflow: hidden;
}

.asr-lang {
  position: absolute;
  top: 20px;
  right: 30px;
  z-index: 10;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.14em;
  user-select: none;
}

.asr-lang .caret {
  font-size: 10px;
  margin-left: 4px;
  opacity: 0.85;
}

.asr-hero-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: 50% 42%;
  background-repeat: no-repeat;
}

.asr-hero-shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      90deg,
      rgba(7, 18, 26, 0.78) 0%,
      rgba(7, 18, 26, 0.40) 26%,
      rgba(7, 18, 26, 0) 52%
    );
}

/* =========================================================
   MAIN GRID
========================================================= */

.asr-grid {
  position: relative;
  z-index: 3;

  display: grid;

  grid-template-columns:
    minmax(0, 1fr)
    minmax(360px, 44%);

  min-height: 72vh;
  width: 100%;
}

/* =========================================================
   LEFT SIDE
========================================================= */

.asr-left {
  min-width: 0;

  display: flex;
  flex-direction: column;
  justify-content: center;

  padding: 9vh 4% 5vh 5%;
}

.asr-left h1 {
  margin: 0;

  color: #fff;

  font-weight: 700;
  line-height: 1.04;

  font-size: clamp(30px, 4.2vw, 56px);

  letter-spacing: 0.005em;

  text-shadow:
    0 2px 22px rgba(0, 0, 0, 0.5);

  max-width: 900px;
}

/* =========================================================
   RIGHT LOGIN
========================================================= */

.asr-right {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;

  width: 100%;
  max-width: 660px;
  min-width: 0;

  margin-left: auto;
  margin-right: 0;

  padding: 54px 24px 60px;

  position: relative;
  z-index: 5;
}

/* =========================================================
   HEADER IMAGE
========================================================= */

.asr-header-img {
  width: 100%;
  max-width: 100%;
  height: auto;
  display: block;
}

.asr-header-fallback {
  width: 100%;

  background: #fff;
  color: var(--teal);

  font-size: 20px;
  font-weight: 700;

  letter-spacing: 0.04em;

  text-align: center;

  padding: 18px 12px;

  text-transform: uppercase;
}

/* =========================================================
   LOGIN PANEL
========================================================= */

.asr-panel {
  width: 100%;

  background: var(--teal-90);

  backdrop-filter: saturate(1.05);

  padding: 30px 34px 36px;

  flex: 1;
}

/* =========================================================
   FORM TITLE
========================================================= */

.asr-form-title {
  margin: 2px 0 4px;

  color: #fff;

  font-weight: 600;

  font-size: clamp(20px, 2vw, 26px);

  letter-spacing: 0.01em;
}

.asr-form-lead {
  margin: 0 0 20px;

  color: rgba(255, 255, 255, 0.70);

  font-size: 14px;
}

/* =========================================================
   LABEL
========================================================= */

.asr-label {
  display: block;

  color: rgba(255, 255, 255, 0.86);

  font-size: 11px;

  letter-spacing: 0.09em;

  text-transform: uppercase;

  margin: 14px 0 6px;
}

/* =========================================================
   INPUT
========================================================= */

.asr-input {
  width: 100%;
  min-height: 42px;

  border: 1px solid #b9c6c9;

  background: #fff;

  border-radius: 2px;

  padding: 11px 12px;

  font-size: 14px;

  color: #1d2a2d;

  outline: none;

  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.asr-input + .asr-input {
  margin-top: 10px;
}

.asr-input::placeholder {
  color: #9aa7aa;
}

.asr-input:focus {
  border-color: var(--gold);

  box-shadow:
    0 0 0 3px rgba(196, 160, 95, 0.28);
}

/* =========================================================
   PASSWORD
========================================================= */

.asr-pw {
  position: relative;
  width: 100%;
}

.asr-pw .asr-input {
  padding-right: 62px;
}

.asr-pw-toggle {
  position: absolute;

  right: 8px;
  top: 50%;

  transform: translateY(-50%);

  background: none;
  border: 0;

  color: #5d7a7f;

  font-size: 12px;
  font-weight: 600;

  letter-spacing: 0.04em;

  cursor: pointer;

  padding: 6px 8px;
}

.asr-pw-toggle:hover {
  color: var(--teal);
}

/* =========================================================
   CHECKBOX ROW
========================================================= */

.asr-row {
  display: flex;

  align-items: center;

  justify-content: space-between;

  margin: 16px 0 22px;

  gap: 12px;

  flex-wrap: wrap;
}

.asr-check {
  display: flex;

  align-items: center;

  gap: 8px;

  color: rgba(255, 255, 255, 0.82);

  font-size: 13px;

  cursor: pointer;
}

.asr-check input {
  accent-color: var(--gold);

  width: 15px;
  height: 15px;

  flex: 0 0 auto;
}

/* =========================================================
   BUTTON
========================================================= */

.asr-btn {
  appearance: none;

  border: 0;

  cursor: pointer;

  border-radius: 2px;

  font-size: 14px;

  font-weight: 700;

  letter-spacing: 0.05em;

  text-transform: uppercase;

  padding: 13px 22px;

  transition:
    transform 0.08s ease,
    filter 0.15s ease,
    opacity 0.15s ease;
}

.asr-btn:active {
  transform: translateY(1px);
}

.asr-btn-primary {
  width: 100%;

  background:
    linear-gradient(
      180deg,
      #cdab66,
      #bd9a55
    );

  color: #143a40;
}

.asr-btn-primary:hover {
  filter: brightness(1.05);
}

.asr-btn-primary:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

/* =========================================================
   LINKS
========================================================= */

.asr-link {
  color: var(--gold-soft);

  font-size: 13px;

  text-decoration: none;
}

.asr-link:hover {
  text-decoration: underline;
}

.asr-or-link {
  background: none;

  border: 0;

  cursor: pointer;

  color: var(--gold-soft);

  font-size: 13px;

  text-decoration: none;

  font-family: inherit;

  padding: 0;
}

.asr-or-link:hover {
  text-decoration: underline;
}

/* =========================================================
   JOIN / ALERT
========================================================= */

.asr-join {
  margin: 18px 0 0;

  color: rgba(255, 255, 255, 0.72);

  font-size: 13px;

  text-align: center;
}

.asr-alert {
  font-size: 13px;

  padding: 11px 14px;

  border-radius: 2px;

  margin: 0 0 10px;

  background: #f2dede;

  color: #a94442;

  border: 1px solid #ebccd1;
}

/* =========================================================
   FOOTER
========================================================= */

.asr-footer {
  background: #fff;

  padding: 44px 5% 26px;

  border-top: 1px solid #ededed;
}

.asr-footer-inner {
  max-width: 1400px;

  margin: 0 auto;

  display: grid;

  grid-template-columns:
    minmax(0, 1fr)
    minmax(0, 1fr);

  gap: 54px;

  align-items: center;
}

.asr-brands {
  width: 100%;

  max-width: 520px;

  display: block;
}

.asr-about p {
  margin: 0 0 12px;

  color: #7c7c7c;

  font-size: 15px;

  line-height: 1.7;
}

.asr-about p:last-child {
  margin-bottom: 0;
}

.asr-copyright {
  max-width: 1400px;

  margin: 28px auto 0;

  color: #a8a8a8;

  font-size: 12px;
}

/* =========================================================
   LANGUAGE
========================================================= */

.asr-lang-wrap {
  position: absolute;

  top: 16px;
  right: 24px;

  z-index: 20;
}

.asr-lang-btn {
  background: none;

  border: 0;

  cursor: pointer;

  color: #fff;

  font-size: 13px;

  font-weight: 600;

  letter-spacing: 0.14em;

  padding: 4px 2px;

  font-family: inherit;
}

.asr-lang-btn .caret {
  font-size: 10px;

  margin-left: 4px;

  opacity: 0.85;
}

.asr-lang-menu {
  position: absolute;

  top: 100%;
  right: 0;

  margin-top: 8px;

  background: #fff;

  border-radius: 4px;

  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.25);

  min-width: 150px;

  overflow: hidden;
}

.asr-lang-option {
  display: block;

  width: 100%;

  text-align: left;

  background: none;

  border: 0;

  cursor: pointer;

  padding: 9px 14px;

  font-size: 13px;

  color: #1d2a2d;

  font-family: inherit;
}

.asr-lang-option:hover,
.asr-lang-option.active {
  background: var(--teal);

  color: #fff;

  font-weight: 600;
}

/* =========================================================
   SUCCESS OVERLAY
========================================================= */

.asr-success-overlay {
  position: fixed;

  inset: 0;

  z-index: 999;

  background:
    linear-gradient(
      160deg,
      var(--teal) 0%,
      #0a3840 100%
    );

  display: flex;

  align-items: center;

  justify-content: center;

  animation: asrFadeIn 0.35s ease;
}

@keyframes asrFadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.asr-success-card {
  text-align: center;

  padding: 40px 32px;

  max-width: 380px;

  width: 90%;
}

.asr-success-hotel-img {
  max-width: 260px;

  width: 100%;

  margin: 0 auto 28px;

  display: block;

  filter: brightness(0) invert(1);

  opacity: 0.92;
}

.asr-success-check {
  width: 72px;

  height: 72px;

  border-radius: 50%;

  background:
    linear-gradient(
      135deg,
      var(--gold),
      var(--gold-soft)
    );

  display: flex;

  align-items: center;

  justify-content: center;

  margin: 0 auto 20px;

  font-size: 36px;

  color: var(--teal);

  box-shadow:
    0 8px 24px rgba(196, 160, 95, 0.45);
}

.asr-success-card h2 {
  margin: 0 0 8px;

  color: #fff;

  font-size: 1.7rem;

  font-weight: 700;

  letter-spacing: 0.01em;
}

.asr-success-card .asr-success-type {
  color: var(--gold-soft);

  font-size: 0.92rem;

  font-weight: 600;

  margin: 0 0 12px;

  letter-spacing: 0.05em;

  text-transform: uppercase;
}

.asr-success-card p {
  color: rgba(255, 255, 255, 0.75);

  font-size: 0.9rem;

  margin: 0 0 28px;
}

.asr-success-spin {
  width: 36px;

  height: 36px;

  border: 3px solid rgba(255, 255, 255, 0.2);

  border-top-color: var(--gold);

  border-radius: 50%;

  animation: asrSpin 0.8s linear infinite;

  margin: 0 auto 12px;
}

@keyframes asrSpin {
  to {
    transform: rotate(360deg);
  }
}

.asr-success-redir {
  color: rgba(255, 255, 255, 0.5);

  font-size: 0.8rem;

  margin: 0;
}

/* =========================================================
   FOCUS
========================================================= */

.asr-root :focus {
  outline: 2px solid var(--gold);

  outline-offset: 2px;
}

.asr-root :focus:not(:focus-visible) {
  outline: none;
}

.asr-root :focus-visible {
  outline: 2px solid var(--gold);

  outline-offset: 2px;
}

/* =========================================================
   DESKTOP 1440+
========================================================= */

@media (min-width: 1440px) {
  .asr-grid {
    grid-template-columns:
      minmax(0, 1fr)
      minmax(480px, 620px);

    min-height: 72vh;
  }

  .asr-right {
    padding-right: 32px;
  }

  .asr-left {
    padding-left: 6%;
  }

  .asr-left h1 {
    font-size: clamp(46px, 3.5vw, 64px);
  }

  .asr-panel {
    padding: 34px 38px 40px;
  }
}

/* =========================================================
   LAPTOP 1200 - 1439
========================================================= */

@media (min-width: 1200px) and (max-width: 1439px) {
  .asr-grid {
    grid-template-columns:
      minmax(0, 1fr)
      minmax(420px, 48%);
  }

  .asr-right {
    padding: 48px 24px 50px;
  }

  .asr-left {
    padding: 8vh 4% 5vh 5%;
  }
}

/* =========================================================
   TABLET LANDSCAPE 1025 - 1199
========================================================= */

@media (min-width: 1025px) and (max-width: 1199px) {
  .asr-grid {
    grid-template-columns:
      minmax(0, 1fr)
      minmax(380px, 46%);
  }

  .asr-left {
    padding: 7vh 3% 5vh 4%;
  }

  .asr-left h1 {
    font-size: clamp(32px, 4vw, 48px);
  }

  .asr-right {
    padding: 42px 20px 45px;
  }

  .asr-panel {
    padding: 28px 28px 32px;
  }

  .asr-form-title {
    font-size: 22px;
  }

  .asr-form-lead {
    font-size: 13px;
  }
}

/* =========================================================
   TABLET 861 - 1024
========================================================= */

@media (min-width: 861px) and (max-width: 1024px) {
  .asr-grid {
    grid-template-columns:
      minmax(0, 1fr)
      minmax(320px, 48%);

    min-height: 70vh;
  }

  .asr-left {
    padding: 7vh 3% 5vh 4%;
  }

  .asr-left h1 {
    font-size: clamp(30px, 4vw, 44px);
  }

  .asr-right {
    padding: 38px 18px 42px;
  }

  .asr-panel {
    padding: 26px 24px 30px;
  }

  .asr-form-title {
    font-size: 22px;
  }

  .asr-form-lead {
    font-size: 13px;
  }
}

/* =========================================================
   MOBILE <= 860
========================================================= */

@media (max-width: 860px) {
  .asr-top {
    overflow: visible;
  }

  .asr-grid {
    grid-template-columns: 1fr;

    min-height: 0;
  }

  .asr-hero-shade {
    background:
      linear-gradient(
        180deg,
        rgba(7, 18, 26, 0.72) 0%,
        rgba(7, 18, 26, 0.32) 45%,
        rgba(7, 18, 26, 0.78) 100%
      );
  }

  .asr-left {
    padding: 70px 6% 40px;

    min-height: 220px;

    align-items: center;

    justify-content: center;

    text-align: center;
  }

  .asr-left h1 {
    width: 100%;

    font-size: clamp(26px, 7vw, 38px);

    text-align: center;
  }

  .asr-right {
    width: 100%;

    max-width: 100%;

    min-height: auto;

    padding: 0 0 40px;

    margin: 0;

    justify-content: flex-start;
  }

  .asr-panel {
    width: 100%;

    background: rgba(14, 72, 82, 0.97);

    padding: 26px 22px 30px;
  }

  .asr-form-title {
    font-size: 22px;
  }

  .asr-footer {
    padding: 32px 6% 22px;
  }

  .asr-footer-inner {
    grid-template-columns: 1fr;

    gap: 24px;
  }

  .asr-brands {
    margin: 0 auto;
  }

  .asr-about p {
    font-size: 13.5px;

    text-align: left;
  }
}

/* =========================================================
   MOBILE 421 - 600
========================================================= */

@media (min-width: 421px) and (max-width: 600px) {
  .asr-lang-wrap {
    top: 12px;

    right: 16px;
  }

  .asr-left {
    padding: 60px 5% 32px;
  }

  .asr-left h1 {
    font-size: clamp(26px, 8vw, 34px);
  }

  .asr-right {
    padding-bottom: 28px;
  }

  .asr-panel {
    padding: 24px 20px 28px;
  }

  .asr-form-title {
    font-size: 21px;
  }

  .asr-form-lead {
    font-size: 13px;

    margin-bottom: 17px;
  }

  .asr-input {
    min-height: 44px;

    font-size: 14px;
  }

  .asr-row {
    margin: 14px 0 20px;
  }

  .asr-btn {
    min-height: 44px;
  }
}

/* =========================================================
   SMALL PHONE <= 420
========================================================= */

@media (max-width: 420px) {
  .asr-lang-wrap {
    top: 10px;

    right: 12px;
  }

  .asr-lang-btn {
    font-size: 11px;
  }

  .asr-left {
    padding: 48px 5% 28px;

    min-height: 180px;
  }

  .asr-left h1 {
    font-size: 24px;

    line-height: 1.08;
  }

  .asr-right {
    padding: 0 0 24px;
  }

  .asr-panel {
    padding: 22px 16px 26px;
  }

  .asr-form-title {
    font-size: 20px;
  }

  .asr-form-lead {
    font-size: 13px;

    margin-bottom: 16px;
  }

  .asr-label {
    font-size: 10.5px;

    margin-top: 12px;
  }

  .asr-input {
    min-height: 42px;

    font-size: 13px;

    padding: 10px 11px;
  }

  .asr-pw .asr-input {
    padding-right: 58px;
  }

  .asr-row {
    flex-direction: column;

    align-items: flex-start;

    gap: 10px;

    margin: 14px 0 18px;
  }

  .asr-btn {
    min-height: 44px;

    padding: 12px 18px;

    font-size: 13px;
  }

  .asr-join {
    font-size: 12px;
  }

  .asr-footer {
    padding: 28px 5% 20px;
  }

  .asr-about p {
    font-size: 13px;
  }

  .asr-copyright {
    font-size: 11px;

    margin-top: 20px;
  }

  .asr-success-card {
    padding: 30px 20px;
  }

  .asr-success-check {
    width: 64px;

    height: 64px;

    font-size: 30px;
  }

  .asr-success-card h2 {
    font-size: 1.45rem;
  }
}

/* =========================================================
   VERY SMALL PHONE <= 360
========================================================= */

@media (max-width: 360px) {
  .asr-left {
    padding: 42px 16px 24px;
  }

  .asr-left h1 {
    font-size: 22px;
  }

  .asr-panel {
    padding: 20px 14px 24px;
  }

  .asr-form-title {
    font-size: 19px;
  }

  .asr-input {
    font-size: 12.5px;
  }

  .asr-check {
    font-size: 12px;
  }

  .asr-link,
  .asr-or-link {
    font-size: 12px;
  }
}

/* =========================================================
   LANDSCAPE MOBILE
========================================================= */

@media (max-width: 860px) and (orientation: landscape) {
  .asr-grid {
    grid-template-columns: 1fr;
  }

  .asr-left {
    min-height: 200px;

    padding: 45px 30px 30px;
  }

  .asr-right {
    min-height: auto;

    padding: 0 0 30px;
  }

  .asr-left h1 {
    font-size: clamp(24px, 5vw, 34px);
  }
}

/* =========================================================
   TOUCH DEVICES
========================================================= */

@media (pointer: coarse) {
  .asr-pw-toggle {
    padding: 9px 11px;

    font-size: 13px;
  }

  .asr-check input {
    width: 18px;

    height: 18px;
  }

  .asr-btn {
    min-height: 44px;
  }

  .asr-lang-btn {
    min-height: 40px;
  }
}

/* =========================================================
   REDUCED MOTION
========================================================= */

@media (prefers-reduced-motion: reduce) {
  .asr-btn,
  .asr-input {
    transition: none;
  }

  .asr-success-overlay,
  .asr-success-spin {
    animation: none;
  }
}

/* =========================================================
   PRINT
========================================================= */

@media print {
  .asr-lang,
  .asr-lang-wrap,
  .asr-success-overlay {
    display: none !important;
  }

  .asr-hero-bg,
  .asr-hero-shade {
    display: none;
  }

  .asr-grid {
    display: block;

    min-height: auto;
  }

  .asr-left {
    padding: 20px;
  }

  .asr-left h1 {
    color: #222;

    text-shadow: none;
  }

  .asr-right {
    max-width: 100%;

    padding: 20px;
  }
    /* =========================================================
   ODX LOGIN TYPE SELECT
========================================================= */

.asr-select {
  width: 100%;
  min-height: 42px;

  border: 1px solid #b9c6c9;
  background: #fff;
  color: #1d2a2d;

  border-radius: 2px;
  padding: 11px 38px 11px 12px;

  font-size: 14px;
  font-family: inherit;

  outline: none;
  cursor: pointer;

  appearance: none;

  background-image:
    linear-gradient(45deg, transparent 50%, #5d7a7f 50%),
    linear-gradient(135deg, #5d7a7f 50%, transparent 50%);
  background-position:
    calc(100% - 16px) 18px,
    calc(100% - 11px) 18px;
  background-size:
    5px 5px,
    5px 5px;
  background-repeat: no-repeat;

  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.asr-select:hover {
  border-color: #8fa3a7;
}

.asr-select:focus {
  border-color: var(--gold);

  box-shadow:
    0 0 0 3px rgba(196, 160, 95, 0.28);
}

.asr-select option {
  color: #1d2a2d;
  background: #fff;
}
}

`;


// Oakwood ASR theme — language selector + translated UI strings. Scoped to
// this theme only (not the rest of the portal). Admin-entered content
// (Heading 1/2 text, the legal Terms body) is NOT auto-translated — only the
// fixed UI chrome below is, since machine-translating arbitrary admin text
// reliably would need a real translation API, which isn't wired up here.
const ASR_LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'hi', label: 'हिन्दी' },
    { code: 'zh', label: '中文' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'ar', label: 'العربية' },
    { code: 'ja', label: '日本語' },
];

const ASR_I18N = {
    en: {
        welcomeTo: 'WELCOME TO', connectInternet: 'Connect to the Internet',
        signInLead: 'Sign in with your ASR membership or room details.',
        field1Label: 'ASR Member Email OR Room Number', field1Placeholder: 'ASR Member Email OR Room Number',
        field2Label: 'ASR Member Password OR Last Name', field2Placeholder: 'ASR Member Password OR Last Name',
        field2LabelEmail: 'ASR Member Password', field2LabelRoom: 'Last Name',
        showPw: 'Show', hidePw: 'Hide',
        connectWithCode: 'Connect with an Access Code',
        enterCodeLead: 'Enter the internet access or conference code provided by the hotel.',
        codePlaceholder: 'Enter your code here',
        agreeToThe: 'I have read and agreed to the', termsConditions: 'Terms & Conditions',
        continueBtn: 'Log In', pleaseWait: 'Please wait…',
        orConnectCode: 'Or, connect with an Access / Conference Code',
        pmsDown: 'PMS is temporarily unavailable.', useCodeInstead: 'Use an access code instead',
        contactReception: ', or contact reception.',
        termsTitle: 'Terms of Use', close: 'Close',
    },
    hi: {
        welcomeTo: 'आपका स्वागत है', connectInternet: 'इंटरनेट से कनेक्ट करें',
        signInLead: 'अपनी ASR सदस्यता या रूम विवरण से साइन इन करें।',
        field1Label: 'ASR सदस्य ईमेल या रूम नंबर', field1Placeholder: 'ASR सदस्य ईमेल या रूम नंबर',
        field2Label: 'ASR सदस्य पासवर्ड या अंतिम नाम', field2Placeholder: 'ASR सदस्य पासवर्ड या अंतिम नाम',
        field2LabelEmail: 'ASR सदस्य पासवर्ड', field2LabelRoom: 'अंतिम नाम',
        showPw: 'दिखाएं', hidePw: 'छुपाएं',
        connectWithCode: 'एक्सेस कोड से कनेक्ट करें',
        enterCodeLead: 'होटल द्वारा दिया गया इंटरनेट एक्सेस या कॉन्फ्रेंस कोड दर्ज करें।',
        codePlaceholder: 'अपना कोड यहाँ दर्ज करें',
        agreeToThe: 'मैंने पढ़ा और सहमत हूं', termsConditions: 'नियम और शर्तें',
        continueBtn: 'लॉग इन', pleaseWait: 'कृपया प्रतीक्षा करें…',
        orConnectCode: 'या, एक्सेस / कॉन्फ्रेंस कोड से कनेक्ट करें',
        pmsDown: 'पीएमएस अस्थायी रूप से अनुपलब्ध है।', useCodeInstead: 'इसके बजाय एक्सेस कोड का उपयोग करें',
        contactReception: ', या रिसेप्शन से संपर्क करें।',
        termsTitle: 'उपयोग की शर्तें', close: 'बंद करें',
    },
    zh: {
        welcomeTo: '欢迎光临', connectInternet: '连接互联网',
        signInLead: '使用您的 ASR 会员资格或房间信息登录。',
        field1Label: 'ASR 会员邮箱或房间号', field1Placeholder: 'ASR 会员邮箱或房间号',
        field2Label: 'ASR 会员密码或姓氏', field2Placeholder: 'ASR 会员密码或姓氏',
        field2LabelEmail: 'ASR 会员密码', field2LabelRoom: '姓氏',
        showPw: '显示', hidePw: '隐藏',
        connectWithCode: '使用访问码连接',
        enterCodeLead: '请输入酒店提供的上网访问码或会议代码。',
        codePlaceholder: '请输入您的代码',
        agreeToThe: '我已阅读并同意', termsConditions: '使用条款',
        continueBtn: '登录', pleaseWait: '请稍候…',
        orConnectCode: '或使用访问码 / 会议代码连接',
        pmsDown: 'PMS 暂时不可用。', useCodeInstead: '改用访问码',
        contactReception: '，或联系前台。',
        termsTitle: '使用条款', close: '关闭',
    },
    es: {
        welcomeTo: 'BIENVENIDO A', connectInternet: 'Conectarse a Internet',
        signInLead: 'Inicie sesión con su membresía ASR o datos de habitación.',
        field1Label: 'Email de miembro ASR O Número de habitación', field1Placeholder: 'Email de miembro ASR O Número de habitación',
        field2Label: 'Contraseña ASR O Apellido', field2Placeholder: 'Contraseña ASR O Apellido',
        field2LabelEmail: 'Contraseña ASR', field2LabelRoom: 'Apellido',
        showPw: 'Mostrar', hidePw: 'Ocultar',
        connectWithCode: 'Conectar con un código de acceso',
        enterCodeLead: 'Ingrese el código de acceso a internet o de conferencia proporcionado por el hotel.',
        codePlaceholder: 'Ingrese su código aquí',
        agreeToThe: 'He leído y acepto los', termsConditions: 'Términos y Condiciones',
        continueBtn: 'Iniciar sesión', pleaseWait: 'Espere por favor…',
        orConnectCode: 'O conéctese con un código de acceso / conferencia',
        pmsDown: 'El PMS no está disponible temporalmente.', useCodeInstead: 'Usar un código de acceso en su lugar',
        contactReception: ', o contacte a recepción.',
        termsTitle: 'Términos de Uso', close: 'Cerrar',
    },
    fr: {
        welcomeTo: 'BIENVENUE À', connectInternet: "Se connecter à Internet",
        signInLead: "Connectez-vous avec votre adhésion ASR ou les détails de votre chambre.",
        field1Label: "Email membre ASR OU Numéro de chambre", field1Placeholder: "Email membre ASR OU Numéro de chambre",
        field2Label: "Mot de passe ASR OU Nom de famille", field2Placeholder: "Mot de passe ASR OU Nom de famille",
        field2LabelEmail: 'Mot de passe ASR', field2LabelRoom: 'Nom de famille',
        showPw: 'Afficher', hidePw: 'Masquer',
        connectWithCode: "Se connecter avec un code d'accès",
        enterCodeLead: "Entrez le code d'accès Internet ou de conférence fourni par l'hôtel.",
        codePlaceholder: 'Entrez votre code ici',
        agreeToThe: "J'ai lu et j'accepte les", termsConditions: "Conditions d'utilisation",
        continueBtn: 'Se connecter', pleaseWait: 'Veuillez patienter…',
        orConnectCode: "Ou connectez-vous avec un code d'accès / conférence",
        pmsDown: 'Le PMS est temporairement indisponible.', useCodeInstead: "Utiliser un code d'accès à la place",
        contactReception: ', ou contactez la réception.',
        termsTitle: "Conditions d'utilisation", close: 'Fermer',
    },
    ar: {
        welcomeTo: 'مرحباً بكم في', connectInternet: 'الاتصال بالإنترنت',
        signInLead: 'سجّل الدخول بعضوية ASR أو تفاصيل الغرفة.',
        field1Label: 'البريد الإلكتروني لعضو ASR أو رقم الغرفة', field1Placeholder: 'البريد الإلكتروني لعضو ASR أو رقم الغرفة',
        field2Label: 'كلمة مرور ASR أو اسم العائلة', field2Placeholder: 'كلمة مرور ASR أو اسم العائلة',
        field2LabelEmail: 'كلمة مرور ASR', field2LabelRoom: 'اسم العائلة',
        showPw: 'إظهار', hidePw: 'إخفاء',
        connectWithCode: 'الاتصال برمز الوصول',
        enterCodeLead: 'أدخل رمز الوصول إلى الإنترنت أو رمز المؤتمر المقدم من الفندق.',
        codePlaceholder: 'أدخل الرمز هنا',
        agreeToThe: 'لقد قرأت ووافقت على', termsConditions: 'الشروط والأحكام',
        continueBtn: 'تسجيل الدخول', pleaseWait: 'يرجى الانتظار…',
        orConnectCode: 'أو اتصل برمز الوصول / المؤتمر',
        pmsDown: 'نظام إدارة الفندق غير متاح مؤقتاً.', useCodeInstead: 'استخدم رمز الوصول بدلاً من ذلك',
        contactReception: '، أو تواصل مع الاستقبال.',
        termsTitle: 'شروط الاستخدام', close: 'إغلاق',
    },
    ja: {
        welcomeTo: 'ようこそ', connectInternet: 'インターネットに接続',
        signInLead: 'ASRメンバーシップまたはお部屋の情報でサインインしてください。',
        field1Label: 'ASRメールアドレス または 部屋番号', field1Placeholder: 'ASRメールアドレス または 部屋番号',
        field2Label: 'ASRパスワード または 姓', field2Placeholder: 'ASRパスワード または 姓',
        field2LabelEmail: 'ASRパスワード', field2LabelRoom: '姓',
        showPw: '表示', hidePw: '非表示',
        connectWithCode: 'アクセスコードで接続',
        enterCodeLead: 'ホテルから提供されたインターネットアクセスコードまたは会議コードを入力してください。',
        codePlaceholder: 'コードを入力',
        agreeToThe: '利用規約を読み同意します', termsConditions: '利用規約',
        continueBtn: 'ログイン', pleaseWait: 'お待ちください…',
        orConnectCode: 'または、アクセス / 会議コードで接続',
        pmsDown: 'PMSは一時的に利用できません。', useCodeInstead: '代わりにアクセスコードを使用',
        contactReception: '、またはフロントにお問い合わせください。',
        termsTitle: '利用規約', close: '閉じる',
    },
};


// Loaded means the SDK's global is there — a tag left by an earlier try that failed
// (or is still loading) is replaced, not taken for loaded.
const loadScript = (src, isReady) => new Promise((resolve) => {
    if (isReady()) return resolve(true);
    document.querySelector(`script[src="${src}"]`)?.remove();
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve(isReady());
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
});

/* ─────────────────────────────────────────────────────────────────────────────
   Inline RFC-1321 MD5 — used for MikroTik HTTP CHAP password hashing.
   Input: Uint8Array → returns lowercase 32-char hex string.
   No external dependency so it works offline inside a captive portal WebView.
───────────────────────────────────────────────────────────────────────────── */
function _md5Hex(input) {
    const sa = (x, y) => { const l = (x & 0xffff) + (y & 0xffff); return (((x >> 16) + (y >> 16) + (l >> 16)) << 16) | (l & 0xffff); };
    const rl = (n, c) => (n << c) | (n >>> (32 - c));
    const cm = (q, a, b, x, s, t) => sa(rl(sa(sa(a, q), sa(x, t)), s), b);
    const ff = (a, b, c, d, x, s, t) => cm((b & c) | ((~b) & d), a, b, x, s, t);
    const gg = (a, b, c, d, x, s, t) => cm((b & d) | (c & (~d)), a, b, x, s, t);
    const hh = (a, b, c, d, x, s, t) => cm(b ^ c ^ d, a, b, x, s, t);
    const ii = (a, b, c, d, x, s, t) => cm(c ^ (b | (~d)), a, b, x, s, t);
    const n8 = input.length;
    const nw = (Math.ceil((n8 + 9) / 64) * 64) / 4;
    const w = new Int32Array(nw);
    for (let i = 0; i < n8; i++) w[i >> 2] |= input[i] << ((i % 4) * 8);
    w[n8 >> 2] |= 0x80 << ((n8 % 4) * 8);
    w[nw - 2] = n8 * 8;
    let a = 0x67452301, b = 0xefcdab89, c = 0x98badcfe, d = 0x10325476;
    for (let i = 0; i < nw; i += 16) {
        const [A, B, C, D] = [a, b, c, d];
        a = ff(a, b, c, d, w[i], 7, -680876936); d = ff(d, a, b, c, w[i + 1], 12, -389564586); c = ff(c, d, a, b, w[i + 2], 17, 606105819); b = ff(b, c, d, a, w[i + 3], 22, -1044525330);
        a = ff(a, b, c, d, w[i + 4], 7, -176418897); d = ff(d, a, b, c, w[i + 5], 12, 1200080426); c = ff(c, d, a, b, w[i + 6], 17, -1473231341); b = ff(b, c, d, a, w[i + 7], 22, -45705983);
        a = ff(a, b, c, d, w[i + 8], 7, 1770035416); d = ff(d, a, b, c, w[i + 9], 12, -1958414417); c = ff(c, d, a, b, w[i + 10], 17, -42063); b = ff(b, c, d, a, w[i + 11], 22, -1990404162);
        a = ff(a, b, c, d, w[i + 12], 7, 1804603682); d = ff(d, a, b, c, w[i + 13], 12, -40341101); c = ff(c, d, a, b, w[i + 14], 17, -1502002290); b = ff(b, c, d, a, w[i + 15], 22, 1236535329);
        a = gg(a, b, c, d, w[i + 1], 5, -165796510); d = gg(d, a, b, c, w[i + 6], 9, -1069501632); c = gg(c, d, a, b, w[i + 11], 14, 643717713); b = gg(b, c, d, a, w[i], 20, -373897302);
        a = gg(a, b, c, d, w[i + 5], 5, -701558691); d = gg(d, a, b, c, w[i + 10], 9, 38016083); c = gg(c, d, a, b, w[i + 15], 14, -660478335); b = gg(b, c, d, a, w[i + 4], 20, -405537848);
        a = gg(a, b, c, d, w[i + 9], 5, 568446438); d = gg(d, a, b, c, w[i + 14], 9, -1019803690); c = gg(c, d, a, b, w[i + 3], 14, -187363961); b = gg(b, c, d, a, w[i + 8], 20, 1163531501);
        a = gg(a, b, c, d, w[i + 13], 5, -1444681467); d = gg(d, a, b, c, w[i + 2], 9, -51403784); c = gg(c, d, a, b, w[i + 7], 14, 1735328473); b = gg(b, c, d, a, w[i + 12], 20, -1926607734);
        a = hh(a, b, c, d, w[i + 5], 4, -378558); d = hh(d, a, b, c, w[i + 8], 11, -2022574463); c = hh(c, d, a, b, w[i + 11], 16, 1839030562); b = hh(b, c, d, a, w[i + 14], 23, -35309556);
        a = hh(a, b, c, d, w[i + 1], 4, -1530992060); d = hh(d, a, b, c, w[i + 4], 11, 1272893353); c = hh(c, d, a, b, w[i + 7], 16, -155497632); b = hh(b, c, d, a, w[i + 10], 23, -1094730640);
        a = hh(a, b, c, d, w[i + 13], 4, 681279174); d = hh(d, a, b, c, w[i], 11, -358537222); c = hh(c, d, a, b, w[i + 3], 16, -722521979); b = hh(b, c, d, a, w[i + 6], 23, 76029189);
        a = hh(a, b, c, d, w[i + 9], 4, -640364487); d = hh(d, a, b, c, w[i + 12], 11, -421815835); c = hh(c, d, a, b, w[i + 15], 16, 530742520); b = hh(b, c, d, a, w[i + 2], 23, -995338651);
        a = ii(a, b, c, d, w[i], 6, -198630844); d = ii(d, a, b, c, w[i + 7], 10, 1126891415); c = ii(c, d, a, b, w[i + 14], 15, -1416354905); b = ii(b, c, d, a, w[i + 5], 21, -57434055);
        a = ii(a, b, c, d, w[i + 12], 6, 1700485571); d = ii(d, a, b, c, w[i + 3], 10, -1894986606); c = ii(c, d, a, b, w[i + 10], 15, -1051523); b = ii(b, c, d, a, w[i + 1], 21, -2054922799);
        a = ii(a, b, c, d, w[i + 8], 6, 1873313359); d = ii(d, a, b, c, w[i + 15], 10, -30611744); c = ii(c, d, a, b, w[i + 6], 15, -1560198380); b = ii(b, c, d, a, w[i + 13], 21, 1309151649);
        a = ii(a, b, c, d, w[i + 4], 6, -145523070); d = ii(d, a, b, c, w[i + 11], 10, -1120210379); c = ii(c, d, a, b, w[i + 2], 15, 718787259); b = ii(b, c, d, a, w[i + 9], 21, -343485551);
        a = sa(a, A); b = sa(b, B); c = sa(c, C); d = sa(d, D);
    }
    return [a, b, c, d].map(n => { let h = ''; for (let i = 0; i < 4; i++) h += ('0' + ((n >> (i * 8)) & 0xff).toString(16)).slice(-2); return h; }).join('');
}

/* MikroTik HTTP CHAP: MD5( byte(chapId) ‖ utf8(password) ‖ hex_bytes(chapChallenge) )
   Returns lowercase hex string suitable for the MikroTik login URL &password= field. */
function _chapHash(chapIdStr, password, chapChallengeHex) {
    try {
        const enc = new TextEncoder();
        const idB = new Uint8Array([parseInt(chapIdStr, 10) & 0xff]);
        const pwB = enc.encode(password);
        // chapChallengeHex is a hex string from MikroTik (32 chars = 16 bytes)
        const chalB = new Uint8Array((chapChallengeHex.match(/.{2}/g) || []).map(h => parseInt(h, 16)));
        const buf = new Uint8Array(idB.length + pwB.length + chalB.length);
        buf.set(idB, 0);
        buf.set(pwB, idB.length);
        buf.set(chalB, idB.length + pwB.length);
        return _md5Hex(buf);
    } catch {
        return password; // fallback to plaintext if parsing fails
    }
}

/**
 * "My Wi-Fi" on a page that has no status box of its own (classic templates, builder
 * pages made before it): the same screens, in a plain card in the branch's branding.
 */
function GuestStatusCard({ status, portalData, look }) {
    const brand = {};
    if (portalData.brandColor) brand['--lp-brand'] = portalData.brandColor;
    if (portalData.brandAccentColor) brand['--lp-accent'] = portalData.brandAccentColor;
    const logo = portalData.template?.design?.logo?.url || portalData.logo;
    return (
        <div className="guest-status-page" style={brand}>
            <div className="guest-status-card">
                {logo && <img src={logo} alt="Logo" className="brand-logo" />}
                <StatusScreen status={status} portalData={portalData} look={look} />
            </div>
        </div>
    );
}

const Landing = () => {
    const [urlSearchParams] = useSearchParams();
    // The gateway's original parameters, even after a reload or a payment
    // provider's return URL dropped them from the address bar (gatewayParams.js).
    const searchParams = useMemo(() => resolveGatewayParams(urlSearchParams), [urlSearchParams]);
    const [loading, setLoading] = useState(true);
    const [portalData, setPortalData] = useState(null);
    const [userType, setUserType] = useState('visitor'); // default
    const userTypeState = userType; // the form's choice — handleSubmit may sign in another way
    const [odxLoginType, setOdxLoginType] = useState('room'); // 'room' | 'asr'
    const [formData, setFormData] = useState({});
    const [activeConflict, setActiveConflict] = useState(null);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    // { mobileHint, channel, expiresAt, token }: a code this device was already sent and
    // has not used — the page opened again (an iPhone closes the sign-in window when the
    // guest goes to WhatsApp for it) comes back at "enter the code".
    const [pendingCode, setPendingCode] = useState(null);
    // { minutes, expiresAt }: internet before sign-in the server opened (branch setting).
    const [preLogin, setPreLogin] = useState(null);
    const [otpTimer, setOtpTimer] = useState(0);
    const [otpMethod, setOtpMethod] = useState('whatsapp'); // 'whatsapp' | 'sms'
    const [resolvedBranchId, setResolvedBranchId] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    // What the gateway sent when no HotWiFi NAS matched it (404 from /landing) —
    // shown so whoever is setting it up can see which MAC/IP to register.
    const [unknownGateway, setUnknownGateway] = useState(null);
    // Persistent inline banner for login failures (wrong password/last name,
    // room not occupied, etc.) — shown alongside the toast since the toast
    // auto-dismisses and is easy to miss on a captive portal.
    const [loginError, setLoginError] = useState('');
    // Voucher UX: when branch is set to 'separate' mode, the main page hides
    // the voucher tab and this flag switches to a dedicated voucher-only screen.
    const [showVoucherPage, setShowVoucherPage] = useState(false);
    // CapitaLand ODX post-login confirmation popup (Login Type / Allow WiFi / API Message)
    const [pmsResult, setPmsResult] = useState(null);
    // Shown after the guest taps OK — "you're connected" screen before the actual redirect
    const [pmsConnected, setPmsConnected] = useState(null);
    const pendingRedirectRef = useRef(null);
    // A visual-builder page with a status box (LandingPageRenderer's statusPanel):
    // what it shows after sign-in — null, { kind: 'connected', details, onContinue }
    // or { kind: 'error', message }.
    const [portalStatus, setPortalStatus] = useState(null);
    // "My Wi-Fi": this browser signed in here before (guestSession.js) — its connection
    // is shown again: still online (with Disconnect), or the summary after Disconnect.
    const storedGuest = useMemo(() => loadGuestSession(), []);
    const [guestStatus, setGuestStatus] = useState(null);
    // Until that is known the page waits, rather than flashing the sign-in form first.
    const [guestChecked, setGuestChecked] = useState(!storedGuest);
    // Prevents double-submit on fast double-tap (mobile) before React re-renders the disabled button
    const submittingRef = useRef(false);
    // After the device limit: the answer's token, sent once with the next sign-in so an
    // OTP sign-in can finish without a new code (the one typed was already used).
    const conflictTokenRef = useRef(null);
    // The last sign-in's override (ISP) — repeated after signing a device out at the limit.
    const lastOverrideRef = useRef(null);
    // Manual approval gate — null | { token, expiresIn, status }
    const [approvalState, setApprovalState] = useState(null);
    const approvalPollRef = useRef(null);

    // ISP payment flow state
    const [ispStep, setIspStep] = useState('otp'); // 'otp' | 'select' | 'info' | 'paying' | 'verifying' | 'done'
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [ispInfo, setIspInfo] = useState({ name: '', phone: '', email: '' });
    const [paymentLoading, setPaymentLoading] = useState(false);

    // ISP OTP gate — mobile must be verified before browsing/buying a plan
    const [ispOtpCode, setIspOtpCode] = useState('');
    const [ispOtpSent, setIspOtpSent] = useState(false);
    const [ispOtpTimer, setIspOtpTimer] = useState(0);
    const [ispOtpLoading, setIspOtpLoading] = useState(false);
    const [ispVerifiedMobile, setIspVerifiedMobile] = useState('');
    // The server's proof the OTP checked out (the purchase and sign-in rest on it),
    // and the plan the number already has running, if any.
    const [ispMobileToken, setIspMobileToken] = useState('');
    const [ispActivePlan, setIspActivePlan] = useState(null);
    // The plan the number had that has ended (the Scenic page says so, offering it again).
    const [ispLastPlan, setIspLastPlan] = useState(null);

    // "Oakwood ASR" theme preset — set when the backend reports CapitaLand/PMS
    // is unreachable (network error or 5xx, not just "guest denied"), so the
    // UI can prompt the guest to fall back to an access/voucher code instead.
    const [asrPmsDown, setAsrPmsDown] = useState(false);
    // "Oakwood ASR" theme preset — password visibility toggle for combined field
    const [showCapitalandPw, setShowCapitalandPw] = useState(false);
    // Generic theme — password visibility toggle for the ASR Password/Last Name field
    const [showGenericPw, setShowGenericPw] = useState(false);
    // "Oakwood ASR" theme preset — language selector state
    const [asrLang, setAsrLang] = useState('en');
    const [showAsrLangMenu, setShowAsrLangMenu] = useState(false);

    useEffect(() => {
        let interval;
        if (ispOtpTimer > 0) {
            interval = setInterval(() => setIspOtpTimer(t => t - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [ispOtpTimer]);

    // OTP Timer Countdown
    useEffect(() => {
        let interval;
        if (otpTimer > 0) {
            interval = setInterval(() => setOtpTimer(t => t - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [otpTimer]);

    // Ruijie EG Cloud Auth parameters (Third-party auth mode)
    const nasIp =
        searchParams.get('nas_ip') ||   // Ruijie Cloud Auth
        searchParams.get('nasip') ||
        searchParams.get('nasIp') ||
        searchParams.get('nasIP');
    const nasMac =
        searchParams.get('nas_mac') ||   // Ruijie Cloud Auth
        searchParams.get('nasmac') ||
        searchParams.get('nasMac');
    const userMac =
        searchParams.get('MA') || // 11os / MikroTik $(mac)
        searchParams.get('client_mac') || // Ruijie Cloud Auth
        searchParams.get('usermac') ||
        searchParams.get('userMac') ||
        searchParams.get('mac') ||
        searchParams.get('wlan_user_mac') || // Ruijie's other parameter templates
        searchParams.get('wlanusermac') ||
        searchParams.get('user_mac');
    const userIp =
        searchParams.get('SIP') || // 11os / MikroTik $(ip)
        searchParams.get('client_ip') || // Ruijie Cloud Auth
        searchParams.get('userip') ||
        searchParams.get('userIp') ||
        searchParams.get('ip') ||
        searchParams.get('wlan_user_ip') ||
        searchParams.get('wlanuserip') ||
        searchParams.get('user_ip');
    const ruijieRedirect =
        searchParams.get('orig_url') || // Reyee EG "Ruijie" parameter template
        searchParams.get('url') ||   // Ruijie Cloud Auth
        searchParams.get('redirect');

    // Ruijie gateway callback params (for granting internet after auth)
    const gwAddress = searchParams.get('gw_address');
    const gwPort = searchParams.get('gw_port') || '2060';
    const gwId = searchParams.get('gw_id');
    // Ruijie's "Ruijie" External Authentication Parameters preset (confirmed
    // directly from a real device's config dialog) sends login_url/logout_url/
    // ssid — none of which map onto gw_address/gw_port/gw_id. login_url is
    // Ruijie's OWN authoritative auth-callback endpoint — using it directly
    // (see doRedirect below) is more robust than reconstructing one from
    // Portal IP/Port, which depends on those being configured correctly on
    // Ruijie's side at all. logout_url/ssid are also sent but nothing here
    // uses them yet — not read, to avoid dead variables; add them back if a
    // logout button or SSID display ever needs them.
    const ruijieLoginUrl = searchParams.get('login_url');

    // MikroTik / generic NAS parameters
    const linkLogin = searchParams.get('link-login');
    const linkOrig = searchParams.get('OS') || searchParams.get('link-orig'); // OS = $(link-orig) in 11os format
    const chapId = searchParams.get('chap-id');
    const chapChallenge = searchParams.get('chap-challenge');

    // MikroTik NAS identifier — UI param is $(server-name) from 11os-style login.html
    const nasIdentifier =
        searchParams.get('UI') || // 11os-style: $(server-name)
        searchParams.get('nasIdentifier') ||
        searchParams.get('nas-identifier') ||
        searchParams.get('nas_identifier') ||
        searchParams.get('nas');

    // Unified resolved values
    const branchId = searchParams.get('branchId') || searchParams.get('branchid') || searchParams.get('bid');
    // Opened without the gateway's parameters (a guest coming back to "My Wi-Fi"): the
    // branch this browser signed in at, for its page and branding.
    const lookupBranchId = branchId || (!nasIp && !nasMac && !nasIdentifier ? storedGuest?.branchId : null);
    const clientIP = userIp || searchParams.get('clientIP') || searchParams.get('clientip');
    const deviceMAC = normalizeGuestMac(userMac || searchParams.get('deviceMAC') || searchParams.get('devicemac')) || '00:00:00:00:00:00';

    // Nomadix: no link-login — the gateway signs the guest in itself once we
    // ask it to (POST /api/portal/nomadix/login → XML RADIUS_LOGIN), then the
    // browser goes on to the page the guest first asked for (OS).
    const isNomadix = portalData?.gatewayType === 'nomadix';
    const signInAtNomadix = async ({ sessionId, radiusUsername, radiusPassword }) => {
        try {
            await portalAPI.nomadixLogin({ sessionId, radiusUsername, radiusPassword, mac: deviceMAC, nasIdentifier });
            // The gateway acknowledges the XML command at once and RADIUS-
            // authenticates right after — give it a moment before the
            // browser asks it for the original page.
            setTimeout(() => { window.location.href = linkOrig || 'http://www.google.com'; }, 1500);
        } catch (err) {
            setLoading(false);
            toast.error(err.response?.data?.message || 'Could not sign you in at the WiFi gateway. Please try again.');
        }
    };

    // No mobile network here: the server opened a few minutes of internet so WhatsApp
    // can bring the code in. Say so — and to come back to this page for it. On an
    // iPhone, leaving the sign-in window can close it: the code is read from the
    // notification instead.
    const notePreLogin = (granted) => {
        if (!granted) return;
        setPreLogin(granted);
        toast.info(isAppleDevice()
            ? `Internet is on for ${granted.minutes} min: the code comes as a WhatsApp notification at the top of the screen — read it there and stay on this page.`
            : `Internet is on for ${granted.minutes} min: open WhatsApp for the code, then come back to this page.`, { autoClose: 8000 });
    };

    const handleSendOTP = async () => {
        // Back at "enter the code" with the number kept on the server: a new code needs it typed.
        if (!formData.mobile && pendingCode) return toast.info('For a new code, tap "Change number" and enter your number again');
        if (!formData.mobile) return toast.warning('Please enter mobile number');
        if (formData.mobile.length < 8) return toast.warning('Invalid mobile number');

        try {
            setLoading(true);
            const res = await portalAPI.sendOTP({
                mobile: formData.mobile,
                branchId: resolvedBranchId,
                method: otpMethod,
                nasIdentifier,
                deviceMAC,
                clientIP,
            });
            if (res.data.success) {
                toast.success(`OTP sent successfully via ${otpMethod === 'whatsapp' ? 'WhatsApp' : 'SMS'}`);
                notePreLogin(res.data.preLogin);
                setPendingCode(null); // the number typed now is the one
                setOtpSent(true);
                setOtpTimer(60); // 60s cooldown
            }
        } catch (err) {
            console.error('OTP Send Error:', err);
            toast.error(err.response?.data?.message || 'Failed to send OTP');
        } finally {
            setLoading(false);
        }
    };

    // ISP gate: send OTP to verify the mobile number before showing plans
    const handleSendIspOtp = async () => {
        if (!ispInfo.phone.trim()) return toast.warning('Please enter your mobile number');
        if (ispInfo.phone.trim().length < 8) return toast.warning('Invalid mobile number');

        try {
            setIspOtpLoading(true);
            const res = await portalAPI.sendOTP({
                mobile: ispInfo.phone.trim(),
                branchId: resolvedBranchId,
                method: otpMethod,
                nasIdentifier,
                deviceMAC,
                clientIP,
            });
            if (res.data.success) {
                toast.success(`OTP sent successfully via ${otpMethod === 'whatsapp' ? 'WhatsApp' : 'SMS'}`);
                notePreLogin(res.data.preLogin);
                setIspOtpSent(true);
                setIspOtpTimer(60);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to send OTP');
        } finally {
            setIspOtpLoading(false);
        }
    };

    // ISP gate: verify OTP only — no session/user created here, just unlocks plan selection
    const handleVerifyIspOtp = async () => {
        if (!ispOtpCode.trim()) return toast.warning('Please enter the OTP');

        try {
            setIspOtpLoading(true);
            const res = await portalAPI.verifyOTP({
                mobile: ispInfo.phone.trim(),
                otp: ispOtpCode.trim(),
                branchId: resolvedBranchId,
                // For the guest's activity log only (the device the number was checked on).
                deviceMAC,
                clientIP,
            });
            if (res.data.success) {
                toast.success('Mobile number verified!');
                setIspVerifiedMobile(ispInfo.phone.trim());
                setIspMobileToken(res.data.mobileToken || '');
                setIspActivePlan(res.data.activePlan || null);
                setIspLastPlan(res.data.lastPlan || null);
                // A plan still running: just connect. Otherwise pick one.
                setIspStep(res.data.activePlan ? 'active' : 'select');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Invalid or expired OTP');
        } finally {
            setIspOtpLoading(false);
        }
    };

    useEffect(() => {
        if (lookupBranchId || nasIp || nasMac || nasIdentifier) {
            fetchLandingData();
        } else {
            setLoading(false);
        }
    }, [lookupBranchId, nasIp, nasMac, nasIdentifier]);

    const fetchLandingData = async () => {
        let reloading = false;
        try {
            setLoading(true);
            setErrorMsg('');
            setUnknownGateway(null);
            const response = await portalAPI.getLanding({ branchId: lookupBranchId, nasIdentifier, clientIP, deviceMAC, nasIp, nasMac });
            const data = response.data.data;
            // Internet before sign-in is open (told to an Apple device only): the page
            // loads once more, so the iPhone sees this Wi-Fi work (appleDevice.js) —
            // the spinner stays meanwhile.
            if (data.preLogin) {
                setPreLogin(data.preLogin);
                if (reloadForOnlineCheck()) {
                    reloading = true;
                    return;
                }
            }
            setPortalData(data);
            setResolvedBranchId(data.branchId || branchId);
            if (data.otpDefaultMethod) setOtpMethod(data.otpDefaultMethod);

            // Determine default user type
            // In 'separate' voucher mode, the main page never lands on the voucher
            // form directly — fall back to the next available login method instead.
            const voucherIsSeparate = data.voucherMode === 'separate';
            if (data.defaultMethod && !(data.defaultMethod === 'voucher' && voucherIsSeparate)) {
                const method = data.defaultMethod;
                if (method === 'voucher' || method === 'access_code' || method === 'access-code') setUserType('visitor');
                else if (method === 'pms') setUserType('guest');
                else if (method === 'otp') setUserType('otp');
                else if (method === 'form' || method === 'free') setUserType('form');
            } else if (data.template && data.template.fields && data.template.fields.length > 0) {
                setUserType('form');
            } else if (voucherIsSeparate) {
                if (data.loginMethods?.includes('pms')) setUserType('guest');
                else if (data.loginMethods?.includes('otp')) setUserType('otp');
            }

            // "Oakwood ASR" theme preset has only one form (ASR Member/Room Number) —
            // force userType to 'guest' last, so it always wins regardless of what
            // the branch's configured defaultMethod resolved to above.
            if (data.template?.themePreset === 'oakwood-asr') {
                setUserType('guest');
            }

            // FIX 2: Removed duplicate `accessCode` key
            const initialData = {
                voucherCode: '',
                roomNumber: '',
                lastName: '',
                asrEmail: '',
                asrPassword: '',
                capitalandId: '',
                capitalandSecret: '',
                accessCode: '',
                mobile: '',
                otp: '',
                employeeId: '',
                agreedToTerms: false
            };

            // Add dynamic fields from template
            if (data.template && data.template.fields) {
                data.template.fields.forEach(field => {
                    initialData[field.name] = '';
                });
            }

            setFormData(initialData);

            // A code already sent to this device and not used yet: straight to "enter the
            // code" — not the start again, and no new code that would void the one copied.
            if (data.pendingCode && data.loginMethods?.includes('otp')) {
                setPendingCode(data.pendingCode);
                setUserType('otp');
                if (data.pendingCode.channel) setOtpMethod(data.pendingCode.channel);
                setOtpSent(true);
                toast.info(`Your code was sent to ${data.pendingCode.mobileHint} — enter it here.`, { autoClose: 6000 });
            }

        } catch (err) {
            console.error('Failed to load portal data', err);
            const msg = err.response?.data?.message || err.message || 'Failed to load portal';
            const isNetErr = !err.response || msg.includes('timeout') || msg.includes('Network');
            setErrorMsg(msg);
            if (err.response?.status === 404) setUnknownGateway(err.response.data?.debug || {});
            // Auto-retry once after 4s on network/timeout errors (server may be restarting)
            if (isNetErr) {
                setTimeout(() => {
                    setErrorMsg('');
                    setLoading(true);
                    fetchLandingData();
                }, 4000);
            }
        } finally {
            if (!reloading) setLoading(false);
        }
    };

    // ── ISP: pay for a plan, then sign this device in ───────────────────
    // The order token is what the gateway's /init and the final sign-in take; a
    // redirect gateway (Cashfree / PhonePe / Paytm, Razorpay in redirect mode) takes the guest away and back,
    // so the token waits in this tab's storage meanwhile.
    const ISP_ORDER_KEY = 'hotwifi.ispOrder';
    const rememberIspOrder = (order) => {
        try { sessionStorage.setItem(ISP_ORDER_KEY, JSON.stringify(order)); } catch { /* storage blocked */ }
    };
    const recallIspOrder = () => {
        try { return JSON.parse(sessionStorage.getItem(ISP_ORDER_KEY) || 'null'); } catch { return null; }
    };
    const forgetIspOrder = () => {
        try { sessionStorage.removeItem(ISP_ORDER_KEY); } catch { /* storage blocked */ }
    };

    // Signs this device in on the number's (now) active plan — the same portal sign-in
    // as every login method: device limit, the gateway handoff, My Wi-Fi.
    const connectIsp = (ispToken) => {
        if (!ispToken) {
            setIspStep('otp');
            toast.error('Please verify your mobile number again.');
            return;
        }
        handleSubmit({ preventDefault: () => {} }, { userType: 'isp', ispToken });
    };

    // Back from a redirect gateway: ask it whether the payment went through, then connect.
    const paymentCallbackHandled = useRef(false);
    useEffect(() => {
        if (!portalData || paymentCallbackHandled.current) return;
        const provider = ['cashfree', 'phonepe', 'paytm', 'razorpay'].find(p => searchParams.get(`${p}_callback`));
        const gatewayOrderId = searchParams.get('order_id');
        if (!provider || !gatewayOrderId) return;
        paymentCallbackHandled.current = true;
        setIspStep('verifying');
        axios.post(`${API_BASE}/public/${provider}/status`, { order_id: gatewayOrderId })
            .then(r => {
                const pending = recallIspOrder();
                if (r.data.success && pending?.dbOrderId === r.data.db_order_id) {
                    setIspStep('done');
                    toast.success('Payment successful! Connecting you…');
                    forgetIspOrder();
                    connectIsp(pending.orderToken);
                } else if (r.data.success) {
                    // Paid — but this tab no longer holds the order (another browser,
                    // storage cleared): the number's plan is active, sign in with its OTP.
                    setIspStep('otp');
                    toast.success('Payment received. Verify your number once more to connect.');
                } else {
                    setIspStep('select');
                    toast.error('Payment not confirmed. Please try again.');
                }
            })
            .catch(() => { setIspStep('select'); toast.error('Could not verify payment.'); });
    }, [portalData]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleISPPayment = async () => {
        if (!selectedPlan) return toast.warning('Please select a plan');
        if (!ispInfo.name.trim()) return toast.warning('Please enter your name');
        return startIspPurchase(selectedPlan, { name: ispInfo.name.trim(), email: ispInfo.email.trim() });
    };

    // Buys `plan` for the verified number — the classic ISP page (above) and the
    // Scenic sign-in page (authContext.scenic.pay) both come here.
    const startIspPurchase = async (plan, { name = '', email = '' } = {}) => {
        if (!ispMobileToken) {
            setIspStep('otp');
            return toast.warning('Please verify your mobile number first');
        }

        setPaymentLoading(true);
        try {
            // The server prices the order from the plan and picks this branch's gateway.
            const { data: order } = await portalAPI.createIspOrder({
                mobileToken: ispMobileToken,
                branchId: resolvedBranchId,
                planId: plan._id,
                customerName: name,
                customerEmail: email,
                deviceMAC,
                clientIP,
                // Back to this very page (it carries the gateway's own parameters).
                returnUrl: window.location.href,
            });

            if (order.free) {
                setIspStep('done');
                toast.success('Plan activated! Connecting you…');
                connectIsp(order.order_token);
                return;
            }

            // No mobile network here: a few minutes of internet so the UPI app or the
            // bank's page works (branch setting — the server says if it opened one).
            const pre = await portalAPI.preLoginForPayment({
                mobileToken: ispMobileToken, branchId: resolvedBranchId, nasIdentifier, deviceMAC, clientIP,
            }).catch(() => null);
            if (pre?.data?.preLogin) setPreLogin(pre.data.preLogin);

            const started = { dbOrderId: order.db_order_id, orderToken: order.order_token };
            rememberIspOrder(started);
            const body = { db_order_id: order.db_order_id, order_token: order.order_token };
            if (order.gateway === 'razorpay') {
                await payWithRazorpay(body, plan.name);
            } else if (order.gateway === 'phonepe') {
                await payWithPhonePe(body);
            } else if (order.gateway === 'paytm') {
                await payWithPaytm(body);
            } else {
                await payWithCashfree(body);
            }
        } catch (err) {
            // Say why — "failed" alone leaves nothing to fix (an HTML error page, a
            // payment script that didn't load, no network).
            const data = err.response?.data;
            const reason = (data && typeof data === 'object' && data.message)
                || (err.response ? `server error ${err.response.status}` : err.message);
            toast.error(reason ? `Payment could not start: ${reason}` : 'Payment initiation failed');
            if (err.response?.status === 401) setIspStep('otp');
        } finally {
            setPaymentLoading(false);
        }
    };

    const payWithCashfree = async (body) => {
        const { data } = await axios.post(`${API_BASE}/public/cashfree/init`, body);
        const loaded = await loadScript('https://sdk.cashfree.com/js/v3/cashfree.js', () => typeof window.Cashfree === 'function');
        if (!loaded) throw new Error('Could not load Cashfree (sdk.cashfree.com)');
        const cashfree = window.Cashfree({ mode: data.environment === 'production' ? 'production' : 'sandbox' });
        // On success the page goes to Cashfree; an answer here is an error.
        const result = await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: '_self' });
        if (result?.error) throw new Error(`Cashfree: ${result.error.message || 'could not open the payment page'}`);
    };

    const payWithRazorpay = async (body, planName) => {
        const { data } = await axios.post(`${API_BASE}/public/razorpay/init`, body);
        const loaded = await loadScript('https://checkout.razorpay.com/v1/checkout.js', () => typeof window.Razorpay === 'function');
        if (!loaded) throw new Error('Could not load Razorpay (checkout.razorpay.com)');
        const rzp = new window.Razorpay({
            key: data.key_id,
            amount: data.amount,
            currency: data.currency,
            order_id: data.rz_order_id,
            name: portalData.branchName || 'HotWiFi',
            description: planName || selectedPlan?.name || '',
            prefill: { name: data.customerName, email: data.customerEmail, contact: data.customerPhone },
            // Redirect mode: a captive sign-in browser can't open Checkout's pop-ups. The
            // server takes the result and sends the guest back here (razorpay_callback).
            ...(data.callback_url ? { callback_url: data.callback_url, redirect: true } : {}),
            handler: async (response) => {
                setIspStep('verifying');
                try {
                    const r = await axios.post(`${API_BASE}/public/razorpay/verify`, {
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                        db_order_id: data.db_order_id,
                    });
                    if (r.data.success) {
                        setIspStep('done');
                        toast.success('Payment successful! Connecting you…');
                        forgetIspOrder();
                        connectIsp(body.order_token);
                    } else { setIspStep('select'); toast.error('Payment verification failed'); }
                } catch { setIspStep('select'); toast.error('Verification error'); }
            },
        });
        rzp.open();
    };

    const payWithPhonePe = async (body) => {
        const { data } = await axios.post(`${API_BASE}/public/phonepe/init`, body);
        if (data.redirect_url) window.location.href = data.redirect_url;
    };

    const payWithPaytm = async (body) => {
        const { data } = await axios.post(`${API_BASE}/public/paytm/init`, body);
        // Paytm's payment page takes the transaction token as a form POST.
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${data.base_url}/theia/api/v1/showPaymentPage?mid=${encodeURIComponent(data.mid)}&orderId=${encodeURIComponent(data.order_id)}`;
        Object.entries({ mid: data.mid, orderId: data.order_id, txnToken: data.txn_token }).forEach(([k, v]) => {
            const inp = document.createElement('input');
            inp.type = 'hidden'; inp.name = k; inp.value = v;
            form.appendChild(inp);
        });
        document.body.appendChild(form);
        form.submit();
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (loginError) setLoginError('');
    };

    const pageShowsStatus = !!portalData?.landingPage && hasStatusPanel(portalData.landingPage.nodes);

    // ── "My Wi-Fi" ──────────────────────────────────────────────────────
    const connectAgain = () => {
        clearGuestSession();
        setGuestStatus(null);
        // Without the gateway's parameters there is nothing to sign in through yet: any
        // plain web address makes the gateway bring the guest back here, with them.
        if (!(nasIp || nasMac || nasIdentifier || linkLogin)) window.location.href = 'http://neverssl.com/';
    };

    const disconnectedStatus = (summary) => ({
        kind: 'disconnected', details: summaryRows(summary), onConnectAgain: connectAgain,
    });

    const disconnectThisDevice = async () => {
        if (!storedGuest) return;
        setGuestStatus((current) => (current ? { ...current, busy: true } : current));
        let summary = null;
        try {
            summary = (await portalAPI.myWifiDisconnect(storedGuest.token)).data.data;
        } catch (_) { /* the gateway's own logout below still ends it */ }
        if (!summary && !storedGuest.logoutUrl) {
            toast.error('Could not disconnect right now. Please try again.');
            setGuestStatus((current) => (current ? { ...current, busy: false } : current));
            return;
        }
        markGuestDisconnected();
        setGuestStatus(disconnectedStatus(summary));
        // The gateway did not confirm (it takes no Disconnect-Request, or the port is
        // blocked): opening its own logout address from this device ends it — on every gateway.
        if (!summary?.confirmed && storedGuest.logoutUrl) {
            setTimeout(() => { window.location.href = storedGuest.logoutUrl; }, 1500);
        }
    };

    const onlineStatus = (summary) => ({
        kind: 'online', details: summaryRows(summary),
        onDisconnect: disconnectThisDevice,
        onContinue: () => { window.location.href = portalData?.redirectURL || 'https://www.google.com'; },
    });

    useEffect(() => {
        if (!storedGuest) return undefined;
        let cancelled = false;
        // Sent here by the gateway itself means it does not count this device as signed
        // in, whatever HotWiFi's record says — then the sign-in form is what is needed.
        const sentByGateway = hasGatewayParams(urlSearchParams);
        portalAPI.myWifi(storedGuest.token)
            .then((res) => {
                if (cancelled) return;
                const summary = res.data.data;
                if (summary.status === 'active' && !sentByGateway) setGuestStatus(onlineStatus(summary));
                else if (summary.status !== 'active' && storedGuest.disconnectedAt) setGuestStatus(disconnectedStatus(summary));
                else if (summary.status !== 'active') clearGuestSession();
            })
            .catch((err) => { if (!cancelled && err.response?.status === 404) clearGuestSession(); })
            .finally(() => { if (!cancelled) setGuestChecked(true); });
        return () => { cancelled = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // "View session details" on the connected screen — what the guest signed in with.
    const sessionDetails = () => {
        const signedInWith = {
            guest: odxLoginType === 'asr' ? formData.asrEmail : formData.roomNumber,
            visitor: formData.voucherCode,
            otp: formData.mobile,
            resident: formData.roomNumber,
            staff: formData.employeeId,
        }[userType];
        return [
            { label: 'Signed in as', value: signedInWith },
            { label: 'Network', value: portalData?.branchName },
            { label: 'Device', value: deviceMAC !== '00:00:00:00:00:00' ? deviceMAC : '' },
            { label: 'IP address', value: clientIP },
        ];
    };

    // `override`: a sign-in started by the page itself rather than the form — the ISP
    // flow once the number has a plan ({ userType: 'isp', ispToken }).
    const handleSubmit = async (e, override = null) => {
        e.preventDefault();
        if (submittingRef.current) return;
        setLoginError('');
        const userType = override?.userType || userTypeState;
        lastOverrideRef.current = override;

        if (!override && !formData.agreedToTerms) {
            toast.warning('Please agree to terms and conditions');
            return;
        }

        // Oakwood ASR theme's guest form is always plain Room Number + Last
        // Name (its capitalandId/capitalandSecret fields double as those) —
        // no ASR-member-email option, regardless of pmsProvider. The separate
        // generic theme's own CapitaLand UI further below still supports the
        // combined ASR Email/Room Number login independently of this.
        const oakwoodTheme = portalData?.template?.themePreset === 'oakwood-asr';

        // --- VALIDATION ---
        if (userType === 'visitor' && !formData.voucherCode) {
            return toast.warning('Please enter Voucher Code');
        }
        if (userType === 'guest') {
            if (oakwoodTheme) {
                if (odxLoginType === 'room') {
                    if (!formData.roomNumber) return toast.warning('Please enter Room Number');
                    if (!formData.lastName)   return toast.warning('Please enter Last Name');
                } else {
                    if (!formData.asrEmail)    return toast.warning('Please enter ASR Member Email');
                    if (!formData.asrPassword) return toast.warning('Please enter ASR Member Password');
                }
            } else if (portalData?.pmsProvider === 'capitaland') {
                if (odxLoginType === 'room') {
                    if (!formData.roomNumber) return toast.warning('Please enter Room Number');
                    if (!formData.lastName) return toast.warning('Please enter Last Name');
                } else {
                    if (!formData.asrEmail) return toast.warning('Please enter ASR Member Email');
                    if (!formData.asrPassword) return toast.warning('Please enter ASR Member Password');
                }
            } else {
                if (!formData.roomNumber) return toast.warning('Please enter Room Number');
                if (!formData.lastName) return toast.warning('Please enter Last Name');
            }
        }
        const conflictToken = conflictTokenRef.current;
        conflictTokenRef.current = null;
        if (userType === 'otp') {
            if (!formData.mobile && !pendingCode) return toast.warning('Mobile number required');
            // FIX 3: Only validate OTP if it has been sent
            if (!conflictToken && !otpSent) return toast.warning('Please request an OTP first');
            if (!conflictToken && !formData.otp) return toast.warning('OTP required');
        }
        if (userType === 'resident') {
            if (!formData.roomNumber) return toast.warning('Please enter Room Number');
            if (!formData.mobile) return toast.warning('Please enter Mobile Number');
        }
        if (userType === 'staff') {
            if (!formData.employeeId) return toast.warning('Please enter Employee ID');
            if (!formData.mobile) return toast.warning('Please enter Mobile Number');
        }
        if (userType === 'form') {
            const templateFields = portalData?.template?.fields || [];
            for (const field of templateFields) {
                if (field.required && !formData[field.name]) {
                    return toast.warning(`${field.label} is required`);
                }
            }
        }

        submittingRef.current = true;

        try {
            setLoading(true);

            let capitalandFields = {};
            if (userType === 'guest' && oakwoodTheme) {
                if (odxLoginType === 'room') {
                    capitalandFields = {
                        capitalandId:     formData.roomNumber,
                        capitalandSecret: formData.lastName,
                        roomNumber:       formData.roomNumber,
                        lastName:         formData.lastName,
                    };
                } else {
                    capitalandFields = {
                        capitalandId:     formData.asrEmail,
                        capitalandSecret: formData.asrPassword,
                        asrEmail:         formData.asrEmail,
                        asrPassword:      formData.asrPassword,
                    };
                }
            } else if (userType === 'guest' && portalData?.pmsProvider === 'capitaland') {
                // Generic ODX theme: dropdown decides login type
                if (odxLoginType === 'room') {
                    capitalandFields = {
                        capitalandId:     formData.roomNumber,
                        capitalandSecret: formData.lastName,
                        roomNumber:       formData.roomNumber,
                        lastName:         formData.lastName,
                    };
                } else {
                    capitalandFields = {
                        capitalandId:     formData.asrEmail,
                        capitalandSecret: formData.asrPassword,
                        asrEmail:         formData.asrEmail,
                        asrPassword:      formData.asrPassword,
                    };
                }
            }

            const payload = {
                ...formData,
                ...capitalandFields,
                userType,
                branchId: resolvedBranchId,
                nasIdentifier,
                deviceMAC,
                clientIP,
                nasIp,
                linkLogin,
                linkOrig: linkOrig || ruijieRedirect || 'http://www.google.com',
                chapId,
                chapChallenge,
                ...(isNomadix ? { gatewayType: 'nomadix' } : {}),
                ...(conflictToken ? { conflictToken } : {}),
                ...(override?.ispToken ? { ispToken: override.ispToken } : {}),
                // The number the waiting code went to, kept on the server.
                ...(userType === 'otp' && !formData.mobile && pendingCode ? { pendingToken: pendingCode.token } : {}),
            };

            const response = await portalAPI.authenticate(payload);

            if (response.data.success) {
                // "My Wi-Fi": this connection's key, so the guest can see or end it later.
                saveGuestSession({
                    token: response.data.data?.guestToken,
                    branchId: resolvedBranchId,
                    logoutUrl: gatewayLogoutUrl(searchParams),
                });
                const radiusUser = response.data.data?.radiusUsername;
                const radiusPass = response.data.data?.radiusPassword;

                // Determine Ruijie gateway target:
                // ruijieLoginUrl (login_url param) is Ruijie's OWN authoritative
                // auth-callback endpoint — confirmed from a real device's "Ruijie"
                // External Authentication Parameters preset — prefer it over
                // reconstructing one, since that depends on Portal IP/Port being
                // configured correctly on Ruijie's side at all.
                // Fallback order below it: gw_address:gw_port (older Ruijie Cloud
                // Auth), then nas_ip:2060 (Ruijie Local/Third-party without login_url).
                const gatewayIp = gwAddress || nasIp;
                const gatewayPort = gwPort || '2060';

                const doRedirect = () => {
                    if (isNomadix) {
                        signInAtNomadix({
                            sessionId: response.data.data?.sessionId,
                            radiusUsername: radiusUser,
                            radiusPassword: radiusPass,
                        });
                        return;
                    }
                    if (ruijieLoginUrl || gatewayIp) {
                        // Redirect browser to Ruijie's auth endpoint with credentials as query params.
                        // Form POST from HTTPS → HTTP is blocked by modern browsers (mixed content),
                        // so we use a navigation redirect which browsers allow. Every parameter
                        // the gateway originally sent goes back with it (gatewayParams.js).
                        window.location.href = buildRuijieHandoffUrl({
                            params: searchParams,
                            loginUrl: ruijieLoginUrl,
                            gatewayIp,
                            gatewayPort,
                            user: radiusUser || formData.voucherCode || formData.mobile || '',
                            password: radiusPass || formData.voucherCode || formData.mobile || '',
                            clientIP,
                            deviceMAC,
                            gwId,
                            redirect: ruijieRedirect || 'http://www.google.com',
                            loginParams: portalData?.gatewayLoginParams,
                        });
                        return;
                    }

                    // MikroTik: when link-login is present, the backend has built a special
                    // URL (linkLogin + username + password + dst) that actually performs the
                    // hotspot login at the router. This MUST take priority — falling back to
                    // linkOrig/ruijieRedirect here would just bounce the guest back to their
                    // original URL without ever authenticating at the router, leaving them
                    // "logged in" in our DB but still blocked by the hotspot firewall.
                    //
                    // Forced to PAP (plaintext) always: the backend stores radiusPassword as
                    // a one-way bcrypt hash, which can only be checked against a plaintext
                    // password — a CHAP digest can never be validated against it. MikroTik
                    // exposes $(chap-id)/$(chap-challenge) in the login page regardless of
                    // whether the hotspot profile's login-by actually includes CHAP, so
                    // using them here caused every non-Samsung login to send a password the
                    // backend could never verify.
                    const buildMikrotikPassword = (plainPass) => plainPass;

                    const redirectUrl = linkLogin
                        ? (response.data.data?.redirectURL || (() => {
                            const user = encodeURIComponent(radiusUser || '');
                            const pass = encodeURIComponent(buildMikrotikPassword(radiusPass || ''));
                            const dst = encodeURIComponent(linkOrig || 'http://www.google.com');
                            return `${linkLogin}?username=${user}&password=${pass}&dst=${dst}`;
                        })())
                        : (ruijieRedirect || linkOrig || response.data.data?.redirectURL || 'http://www.google.com');

                    if (!radiusUser || !radiusPass) {
                        console.error('[Portal] Missing RADIUS credentials — username empty:', !radiusUser, 'password empty:', !radiusPass);
                    }
                    console.log('[Portal] Redirecting to hotspot:', redirectUrl.replace(/password=[^&]*/i, 'password=***'));
                    window.location.href = redirectUrl;
                };

                // A builder page with a status box says "You're connected" first; the gateway
                // takes the guest on after a few seconds, or at once on Continue.
                const showConnected = () => {
                    let redirected = false;
                    const go = () => {
                        if (redirected) return;
                        redirected = true;
                        clearTimeout(timer);
                        doRedirect();
                    };
                    const timer = setTimeout(go, CONNECTED_SCREEN_MS);
                    setPortalStatus({
                        kind: 'connected', details: sessionDetails(), onContinue: go,
                        ...(response.data.data?.guestToken ? { manageHost: window.location.host } : {}),
                    });
                };

                // Manual approval gate — admin must tap Approve in ntfy before internet is granted
                if (response.data.requiresApproval) {
                    const approvalToken = response.data.approvalToken;
                    const expiresIn = response.data.expiresIn || 60;
                    setApprovalState({ token: approvalToken, expiresIn, status: 'pending', secondsLeft: expiresIn });
                    setLoading(false);

                    let secondsLeft = expiresIn;
                    approvalPollRef.current = setInterval(async () => {
                        secondsLeft -= 3;
                        setApprovalState(prev => prev ? { ...prev, secondsLeft: Math.max(0, secondsLeft) } : null);
                        try {
                            const poll = await axios.get(`/api/pms/approval/${approvalToken}`);
                            const { status, authResult } = poll.data;
                            if (status === 'approved' && authResult) {
                                clearInterval(approvalPollRef.current);
                                setApprovalState({ token: approvalToken, status: 'approved' });
                                setTimeout(() => {
                                    setApprovalState(null);
                                    setPmsConnected({ loginType: authResult.pmsLoginType || 'Room Number Login' });
                                    const rUser = authResult.radiusUsername || '';
                                    const rPass = authResult.radiusPassword || '';
                                    if (isNomadix) {
                                        signInAtNomadix({ sessionId: authResult.sessionId, radiusUsername: rUser, radiusPassword: rPass });
                                        return;
                                    }
                                    const redirectUrl = linkLogin
                                        ? `${linkLogin}?username=${encodeURIComponent(rUser)}&password=${encodeURIComponent(rPass)}&dst=${encodeURIComponent(linkOrig || 'http://www.google.com')}`
                                        : (linkOrig || authResult.redirectURL || 'http://www.google.com');
                                    setTimeout(() => { window.location.href = redirectUrl; }, 1800);
                                }, 1500);
                            } else if (status === 'rejected' || status === 'expired') {
                                clearInterval(approvalPollRef.current);
                                setApprovalState({ token: approvalToken, status });
                            }
                        } catch (_) { /* ignore poll errors */ }
                    }, 3000);
                    return;
                }

                // CapitaLand ODX: when positive skip the debug popup entirely and go
                // straight to the branded success screen → then redirect.
                // The debug popup is only shown for negative/error results for diagnosis.
                const pmsLoginType = response.data.data?.pmsLoginType;
                const pmsAllowWifi = response.data.data?.pmsAllowWifi;
                const pmsApiMessage = response.data.data?.pmsApiMessage || 'Success';
                if (pmsLoginType) {
                    if (pmsAllowWifi && pageShowsStatus) {
                        showConnected();
                    } else if (pmsAllowWifi) {
                        // Positive: branded success screen → redirect (no debug popup)
                        setPmsConnected({ loginType: pmsLoginType });
                        setTimeout(doRedirect, 2000);
                    } else {
                        // Negative: show ODX details popup for diagnosis, no redirect
                        setPmsResult({ loginType: pmsLoginType, allowWifi: pmsAllowWifi, apiMessage: pmsApiMessage });
                        setTimeout(() => setPmsResult(null), 3000);
                    }
                } else if (pageShowsStatus) {
                    showConnected();
                } else {
                    toast.success('Connected! Connecting to internet...');
                    setTimeout(doRedirect, 1200);
                }
            }
        } catch (err) {
            const status = err.response?.status;
            const data = err.response?.data || {};
            const failureType = data.failureType;

            console.error('[Portal] Authentication failed', {
                status,
                failureType,
                correlationId: data.correlationId,
            });

            if (status === 409 && data.multipleLogin) {
                setActiveConflict(data);
                return;
            }

            // An ISP sign-in that didn't go through: off the "connecting" screen, with why.
            if (override?.userType === 'isp') {
                setIspStep(ispMobileToken ? (ispActivePlan ? 'active' : 'select') : 'otp');
                toast.error(data.message || 'Could not connect. Please try again.');
                return;
            }

            // The sign-in system did not answer (no reply at all, a server error, or the
            // hotel system down): a builder page with a status box says so in full.
            if (pageShowsStatus && (!err.response || status >= 500 || failureType === 'PMS_UNAVAILABLE')) {
                setPortalStatus({
                    kind: 'error',
                    message: failureType === 'PMS_UNAVAILABLE'
                        ? 'The hotel system didn’t respond. Wait a moment and try again.'
                        : 'The sign-in system didn’t respond. Wait a moment and try again.',
                });
                return;
            }

            switch (failureType) {
                case 'PMS_UNAVAILABLE':
                    setLoginError(
                        data.message ||
                        'Hotel system is temporarily unavailable. Please try again in a moment.'
                    );
                    if (portalData?.loginMethods?.includes('voucher')) {
                        setAsrPmsDown(true);
                    }
                    toast.warning(
                        data.message ||
                        'Hotel system is temporarily unavailable. Please try again in a moment.'
                    );
                    return;

                case 'PMS_VALIDATION_FAILED':
                    setLoginError(
                        data.message ||
                        'Room number or surname is incorrect.'
                    );
                    toast.error(
                        data.message ||
                        'Room number or surname is incorrect.'
                    );
                    return;

                case 'MULTIPLE_DEVICES':
                    setLoginError(
                        data.message ||
                        'Maximum connected devices reached.'
                    );
                    toast.warning(
                        data.message ||
                        'Maximum connected devices reached.'
                    );
                    return;

                case 'AUTH_REJECTED':
                default:
                    setLoginError(
                        data.message ||
                        'Authentication failed. Please check your details.'
                    );
                    toast.error(
                        data.message ||
                        'Authentication failed. Please check your details.'
                    );
                    return;
            }
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleLogoutOldSession = async (sessionId) => {
        const conflictToken = activeConflict?.conflictToken;
        try {
            setLoading(true);
            await portalAPI.logoutOldSession({
                sessionId,
                conflictToken,
                newDeviceMAC: deviceMAC,
                clientIP
            });
        } catch (err) {
            setLoading(false);
            toast.error(err.response?.data?.message || 'Failed to disconnect old session');
            return;
        }
        // That device is off: finish signing this one in — no new OTP needed.
        toast.success('Old device signed out. Connecting this one…');
        setActiveConflict(null);
        setLoading(false);
        conflictTokenRef.current = conflictToken || null;
        handleSubmit({ preventDefault: () => {} }, lastOverrideRef.current);
    };

    if ((loading && !portalData) || !guestChecked) {
        return (
            <div className="portal-loading">
                <div className="spinner"></div>
                <p>Establishing secure connection...</p>
            </div>
        );
    }

    if (!portalData) {
        const isNetworkErr = errorMsg && (errorMsg.includes('timeout') || errorMsg.includes('Network') || errorMsg.includes('network'));
        return (
            <div className="portal-container error">
                <div className="error-card">
                    <i className="fas fa-wifi fa-3x mb-4 text-gray-400"></i>
                    <h2>Welcome</h2>
                    {errorMsg ? (
                        <>
                            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                                {isNetworkErr
                                    ? 'Unable to reach the login server. Please wait a moment and try again.'
                                    : unknownGateway
                                        ? 'This WiFi gateway is not set up in HotWiFi yet. Please contact the reception.'
                                        : 'Unable to load the login page. Please try reconnecting to the WiFi.'}
                            </p>
                            {unknownGateway && (unknownGateway.nasMac || unknownGateway.nasIp || unknownGateway.nasIdentifier) && (
                                <p className="unknown-gateway-details" style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '0.5rem', fontFamily: 'monospace' }}>
                                    {[
                                        unknownGateway.nasMac && `Gateway MAC ${unknownGateway.nasMac}`,
                                        unknownGateway.nasIp && `IP ${unknownGateway.nasIp}`,
                                        unknownGateway.nasIdentifier && `NAS ID ${unknownGateway.nasIdentifier}`,
                                    ].filter(Boolean).join(' · ')}
                                </p>
                            )}
                            <button
                                onClick={() => { setErrorMsg(''); setLoading(true); fetchLandingData(); }}
                                style={{ marginTop: '1rem', padding: '0.6rem 1.5rem', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '0.95rem' }}
                            >
                                Try Again
                            </button>
                        </>
                    ) : (
                        <p>Please connect to a valid hotspot network to continue.</p>
                    )}
                </div>
            </div>
        );
    }

    // The Scenic sign-in page's live flow (Portal/scenic/ScenicPortal.jsx): this
    // branch's real WhatsApp / SMS one-time-code sign-in — for an ISP, the plan
    // purchase and its dashboard. Only on the hotspot (a real device MAC came with
    // the page); anywhere else the page runs its own preview, sending and charging
    // nothing. A plain object of this component's own state and handlers — no hooks.
    function scenicFlow() {
        // (deviceMAC falls back to all zeros when no gateway sent the page.)
        if (!/^([0-9a-f]{2}:){5}[0-9a-f]{2}$/i.test(String(deviceMAC || '')) || /^(00:){5}00$/.test(deviceMAC)) return null;
        const isp = portalData.organizationType === 'isp';
        const openPreLogin = preLogin && new Date(preLogin.expiresAt) > new Date() ? preLogin : null;
        if (isp) {
            const ispStepView = { otp: ispOtpSent ? 'code' : 'mobile', select: 'plan', info: 'plan', active: 'active', verifying: 'verifying', done: 'online' };
            return {
                live: true,
                mode: 'isp',
                phone: ispInfo.phone,
                setPhone: (v) => setIspInfo((p) => ({ ...p, phone: v })),
                channel: otpMethod, setChannel: setOtpMethod, channels: portalData.otpChannels,
                codeSent: ispOtpSent, timer: ispOtpTimer,
                code: ispOtpCode, setCode: setIspOtpCode,
                busy: ispOtpLoading || paymentLoading || loading,
                sendCode: handleSendIspOtp,
                verify: handleVerifyIspOtp,
                changeNumber: () => { setIspOtpSent(false); setIspOtpCode(''); },
                step: ispStepView[ispStep] || 'mobile',
                setIspStep,
                activePlan: ispActivePlan,
                lastPlan: ispLastPlan,
                connectActive: () => connectIsp(ispMobileToken),
                pay: (plan, info) => startIspPurchase(plan, info),
                preLogin: openPreLogin,
            };
        }
        return {
            live: true,
            mode: 'otp',
            phone: formData.mobile || '',
            // The number shown at "enter the code" — masked when it is the waiting code's.
            phoneHint: !formData.mobile && pendingCode ? pendingCode.mobileHint : null,
            setPhone: (v) => setFormData((p) => ({ ...p, mobile: v })),
            channel: otpMethod, setChannel: setOtpMethod, channels: portalData.otpChannels,
            codeSent: otpSent, timer: otpTimer,
            code: formData.otp || '', setCode: (v) => setFormData((p) => ({ ...p, otp: v })),
            busy: loading,
            sendCode: handleSendOTP,
            verify: () => handleSubmit({ preventDefault: () => {} }, { userType: 'otp' }),
            changeNumber: () => { setOtpSent(false); setPendingCode(null); setFormData((p) => ({ ...p, otp: '' })); },
            step: otpSent ? 'code' : 'mobile',
            preLogin: openPreLogin,
        };
    }

    // --- VISUAL LANDING PAGE BUILDER — opt-in, additive (Phase 4) ---
    // portalData.landingPage is only ever present when the branch has
    // explicitly opted in (Branch.landingPageId set + published — see
    // backend/controllers/portal.controller.js's getLandingPage) and is
    // absent for every other branch, so this branch never runs for
    // existing branches and the two paths below (legacy 'custom'/
    // 'oakwood-asr') are completely unchanged. Placed here specifically
    // because every hook in this component has already run by this point
    // (nothing below line ~2540 calls a hook), so an early return here
    // does not violate the rules of hooks.
    if (portalData.landingPage) {
        const pageNodes = portalData.landingPage.nodes || [];
        if (guestStatus && !portalStatus && !hasStatusPanel(pageNodes)) {
            return <GuestStatusCard status={guestStatus} portalData={portalData} look={pageLookOf(pageNodes)} />;
        }
        return (
            <LandingPageRenderer
                nodes={pageNodes}
                portalData={portalData}
                authContext={{
                    formData, handleInputChange, handleSubmit,
                    userType, setUserType,
                    odxLoginType, setOdxLoginType,
                    showGenericPw, setShowGenericPw,
                    otpMethod, setOtpMethod, otpSent, otpTimer, handleSendOTP,
                    loading, showTermsModal, setShowTermsModal, loginError,
                    status: portalStatus || guestStatus, clearStatus: () => setPortalStatus(null),
                    scenic: scenicFlow(),
                }}
            />
        );
    }

    // --- TEMPLATE & DESIGN EXTRACTION ---
    const template = portalData.template || {};
    const design = template.design || {};

    const fontName = design.fontName || 'Inter';
    const primaryColor = design.button?.color || '#0ea5e9';
    const buttonTextColor = design.button?.textColor || '#ffffff';
    const heading1Color = design.heading1?.color || '#111827';
    const heading2Color = design.heading2?.color || '#6b7280';
    const logoUrl = design.logo?.url || portalData.logo;
    const partnerBannerUrl = design.partnerBanner?.url;
    const backgroundImageUrl = design.backgroundImage?.url;
    const footerStripUrl = design.footerStrip?.url;

    const containerStyle = {
        fontFamily: `${fontName}, sans-serif`,
        ...(backgroundImageUrl ? {
            backgroundImage: `url(${backgroundImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
        } : {})
    };
    const btnStyle = { backgroundColor: primaryColor, color: buttonTextColor };
    const linkStyle = { color: primaryColor };

    const DefaultLogo = () => (
        <div className="default-brand-icon" style={{ borderColor: primaryColor, color: primaryColor }}>
            <i className="fas fa-wifi"></i>
        </div>
    );

    // Terms & Conditions modal — shared across every theme below (generic,
    // dedicated voucher page, Oakwood ASR). Uses the branch's own configured
    // text (Branches → Edit → Terms and Conditions) when set, otherwise a
    // generic captive-portal legal default. Previously the "Terms & Conditions"
    // link on every theme was a stub that just showed a toast — this is the
    // actual modal.
    const termsModalT = ASR_I18N[asrLang] || ASR_I18N.en;
    const termsModal = showTermsModal && (
        <TermsModal
            text={portalData.termsAndConditions}
            title={termsModalT.termsTitle}
            closeLabel={termsModalT.close}
            onClose={() => setShowTermsModal(false)}
        />
    );

    // --- RENDER ---

    // 0. "My Wi-Fi" — this browser's own connection
    if (guestStatus) {
        return <GuestStatusCard status={guestStatus} portalData={portalData} look={null} />;
    }

    // 1. Conflict State
    if (activeConflict) {
        return (
            <div className="portal-container" style={containerStyle}>
                <div className="conflict-card card">
                    <div className="card-header text-center">
                        <i className="fas fa-user-lock fa-3x mb-4" style={{ color: primaryColor }}></i>
                        <h2 style={{ color: heading1Color }}>Device Limit Reached</h2>
                        <p style={{ color: heading2Color }}>
                            {activeConflict.message || `You have reached the maximum allowed devices (${activeConflict.policy?.maxDevices}).`}
                        </p>
                    </div>
                    <div className="card-body">
                        <p className="text-secondary text-center mb-4">Sign out one device to connect this one:</p>
                        <div className="existing-sessions">
                            {(activeConflict.existingSessions || []).map(session => (
                                <div key={session._id} className="session-item">
                                    <div className="session-info">
                                        <p className="device-mac"><i className="fas fa-laptop mr-2"></i>{session.deviceMac || session.deviceMAC}</p>
                                        <p className="session-start">Started: {formatTime(session.startTime)}</p>
                                    </div>
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleLogoutOldSession(session._id)}
                                    >
                                        Logout
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="card-footer">
                        <button className="btn btn-ghost w-full" onClick={() => setActiveConflict(null)}>
                            Back to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 1b. Dedicated Voucher Login Page (Branch voucherMode === 'separate')
    if (showVoucherPage && portalData.voucherMode === 'separate') {
        return (
            <div className="portal-container" style={containerStyle}>
                <div className="portal-card card animate-fade-in-up">
                    <div className="portal-header">
                        {logoUrl ? (
                            <img src={logoUrl} alt="Logo" className="brand-logo" />
                        ) : <DefaultLogo />}
                        <h1 style={{ color: heading1Color }}>Voucher Login</h1>
                        <p className="welcome-msg" style={{ color: heading2Color }}>
                            Enter your voucher code to connect
                        </p>
                    </div>

                    <form className="portal-form" onSubmit={handleSubmit}>
                        <div className="form-group animate-fade-in">
                            <label>Voucher Code</label>
                            <div className="input-with-icon">
                                <i className="fas fa-key"></i>
                                <input
                                    type="text"
                                    name="voucherCode"
                                    className="input"
                                    placeholder="Enter access code"
                                    required
                                    value={formData.voucherCode}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="terms-container">
                            <label className="checkbox-wrapper">
                                <input
                                    type="checkbox"
                                    name="agreedToTerms"
                                    checked={formData.agreedToTerms}
                                    onChange={handleInputChange}
                                />
                                <span className="checkbox-text">
                                    I agree to the <a href="#" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }} style={linkStyle}>Terms & Conditions</a>
                                </span>
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary btn-lg w-full mt-4"
                            disabled={loading}
                            style={btnStyle}
                        >
                            {loading
                                ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                : (design.button?.text || 'Connect')
                            }
                        </button>
                    </form>

                    <div className="voucher-back-row">
                        <a href="#" onClick={(e) => { e.preventDefault(); setShowVoucherPage(false); }} style={linkStyle}>
                            <i className="fas fa-arrow-left"></i> Back to login
                        </a>
                    </div>

                    <div className="portal-footer">
                        <p>© {new Date().getFullYear()} {portalData.branchName || 'Network'}. {PORTAL_CREDIT}</p>
                    </div>
                </div>
                {termsModal}
            </div>
        );
    }

    // 2a. ISP Portal — plan selection + payment flow
    if (portalData.organizationType === 'isp') {
        const plans = portalData.plans || [];

        return (
            <div className="portal-container" style={containerStyle}>
                <div className="portal-card card animate-fade-in-up">
                    {/* Header */}
                    <div className="portal-header">
                        {logoUrl ? <img src={logoUrl} alt="Logo" className="brand-logo" /> : <DefaultLogo />}
                        <h1 style={{ color: heading1Color }}>
                            {design.heading1?.text || portalData.branchName || 'Internet Plans'}
                        </h1>
                        <p className="welcome-msg" style={{ color: heading2Color }}>
                            {design.heading2?.text || 'Select a plan and pay to get connected'}
                        </p>
                    </div>

                    {/* Step: Verify Mobile (OTP gate — must pass before browsing/buying a plan) */}
                    {ispStep === 'otp' && (
                        <div className="animate-fade-in">
                            <div className="otp-method-toggle">
                                <button
                                    type="button"
                                    className={`otp-method-btn ${otpMethod === 'whatsapp' ? 'active' : ''}`}
                                    onClick={() => setOtpMethod('whatsapp')}
                                    style={otpMethod === 'whatsapp' ? { borderColor: primaryColor, color: primaryColor } : {}}
                                >
                                    <i className="fab fa-whatsapp"></i> WhatsApp
                                </button>
                                <button
                                    type="button"
                                    className={`otp-method-btn ${otpMethod === 'sms' ? 'active' : ''}`}
                                    onClick={() => setOtpMethod('sms')}
                                    style={otpMethod === 'sms' ? { borderColor: primaryColor, color: primaryColor } : {}}
                                >
                                    <i className="fas fa-sms"></i> SMS
                                </button>
                            </div>
                            <div className="form-group">
                                <label>Mobile Number</label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <div className="input-with-icon" style={{ flexGrow: 1 }}>
                                        <i className="fas fa-mobile-alt"></i>
                                        <input
                                            type="tel"
                                            className="input"
                                            placeholder="Enter mobile number"
                                            value={ispInfo.phone}
                                            onChange={e => setIspInfo(p => ({ ...p, phone: e.target.value }))}
                                            disabled={ispOtpSent}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleSendIspOtp}
                                        disabled={ispOtpLoading || ispOtpTimer > 0}
                                        style={{ minWidth: '100px', ...btnStyle, opacity: (ispOtpLoading || ispOtpTimer > 0) ? 0.7 : 1 }}
                                    >
                                        {ispOtpTimer > 0 ? `${ispOtpTimer}s` : (ispOtpSent ? 'Resend' : 'Send OTP')}
                                    </button>
                                </div>
                            </div>

                            {ispOtpSent && (
                                <div className="form-group animate-fade-in">
                                    <label>Enter OTP</label>
                                    <div className="input-with-icon">
                                        <i className="fas fa-lock"></i>
                                        <input
                                            type="text"
                                            className="input"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            placeholder="6-digit Code"
                                            value={ispOtpCode}
                                            onChange={e => setIspOtpCode(e.target.value)}
                                            maxLength={6}
                                        />
                                    </div>
                                </div>
                            )}

                            <button
                                type="button"
                                className="btn btn-primary btn-lg w-full mt-4"
                                style={btnStyle}
                                disabled={ispOtpLoading || !ispOtpSent}
                                onClick={handleVerifyIspOtp}
                            >
                                {ispOtpLoading
                                    ? <span className="spinner-border spinner-border-sm"></span>
                                    : 'Verify & Continue'
                                }
                            </button>
                        </div>
                    )}

                    {/* Step: The number already has a plan running — just connect */}
                    {ispStep === 'active' && ispActivePlan && (
                        <div className="isp-success animate-fade-in">
                            <div className="isp-success-icon" style={{ color: primaryColor }}>
                                <i className="fas fa-wifi"></i>
                            </div>
                            <h3 style={{ color: heading1Color }}>{ispActivePlan.name || 'Your plan'} is active</h3>
                            <p style={{ color: heading2Color }}>
                                Valid till {new Date(ispActivePlan.validTill).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                            </p>
                            <button
                                type="button"
                                className="btn btn-primary btn-lg w-full mt-4"
                                style={btnStyle}
                                disabled={loading}
                                onClick={() => connectIsp(ispMobileToken)}
                            >
                                {loading ? <span className="spinner-border spinner-border-sm"></span> : <><i className="fas fa-plug mr-2"></i>Connect</>}
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost w-full mt-2"
                                style={{ color: heading2Color }}
                                onClick={() => setIspStep('select')}
                            >
                                Buy another plan
                            </button>
                        </div>
                    )}

                    {/* Step: Verifying */}
                    {ispStep === 'verifying' && (
                        <div className="isp-verifying">
                            <div className="spinner"></div>
                            <p>Verifying payment...</p>
                        </div>
                    )}

                    {/* Step: Done */}
                    {ispStep === 'done' && (
                        <div className="isp-success">
                            <div className="isp-success-icon" style={{ color: primaryColor }}>
                                <i className="fas fa-check-circle"></i>
                            </div>
                            <h3 style={{ color: heading1Color }}>{ispActivePlan && !selectedPlan ? 'Connecting…' : 'Payment Successful!'}</h3>
                            <p style={{ color: heading2Color }}>Granting internet access...</p>
                        </div>
                    )}

                    {/* Step: Select Plan */}
                    {ispStep === 'select' && (
                        <div className="isp-plans-section animate-fade-in">
                            {plans.length === 0 ? (
                                <p className="isp-no-plans">No plans available. Please contact support.</p>
                            ) : (
                                <div className="isp-plans-grid">
                                    {plans.map(plan => {
                                        const price = plan.pricing?.amount ?? 0;
                                        const isFree = plan.pricing?.isFree || price === 0;
                                        const dlSpeed = plan.bandwidth?.downloadSpeed;
                                        const totalData = plan.dataLimit?.total;
                                        const validity = plan.validity;
                                        const validityLabel = validity
                                            ? `${validity.duration} ${validity.unit}`
                                            : null;
                                        return (
                                            <div
                                                key={plan._id}
                                                className={`isp-plan-card ${selectedPlan?._id === plan._id ? 'selected' : ''}`}
                                                style={selectedPlan?._id === plan._id ? { borderColor: primaryColor, background: `${primaryColor}10` } : {}}
                                                onClick={() => setSelectedPlan(plan)}
                                            >
                                                <div className="plan-name" style={{ color: heading1Color }}>{plan.name}</div>
                                                <div className="plan-price" style={{ color: primaryColor }}>
                                                    {isFree ? 'Free' : `₹${price}`}
                                                    {validityLabel && <span className="plan-price-sub"> / {validityLabel}</span>}
                                                </div>
                                                <div className="plan-features">
                                                    {totalData > 0 && <span><i className="fas fa-database"></i> {totalData >= 1024 ? `${(totalData / 1024).toFixed(0)} GB` : `${totalData} MB`}</span>}
                                                    {dlSpeed && <span><i className="fas fa-tachometer-alt"></i> {dlSpeed >= 1024 ? `${(dlSpeed / 1024).toFixed(0)} Mbps` : `${dlSpeed} Kbps`}</span>}
                                                    {validityLabel && <span><i className="fas fa-calendar"></i> {validityLabel}</span>}
                                                    {plan.description && <span><i className="fas fa-info-circle"></i> {plan.description}</span>}
                                                </div>
                                                {selectedPlan?._id === plan._id && (
                                                    <div className="plan-selected-tick" style={{ color: primaryColor }}>
                                                        <i className="fas fa-check-circle"></i>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {selectedPlan && (
                                <button
                                    className="btn btn-primary btn-lg w-full mt-4"
                                    style={btnStyle}
                                    onClick={() => setIspStep('info')}
                                >
                                    Continue with {selectedPlan.name}
                                    {selectedPlan.pricing?.amount > 0 ? ` — ₹${selectedPlan.pricing.amount}` : ' (Free)'}
                                    <i className="fas fa-arrow-right ml-2"></i>
                                </button>
                            )}
                        </div>
                    )}

                    {/* Step: User Info */}
                    {ispStep === 'info' && (
                        <div className="animate-fade-in">
                            <div className="isp-selected-plan-summary" style={{ borderColor: primaryColor }}>
                                <i className="fas fa-wifi" style={{ color: primaryColor }}></i>
                                <div>
                                    <strong style={{ color: heading1Color }}>{selectedPlan.name}</strong>
                                    {selectedPlan.pricing?.amount > 0 && <span style={{ color: primaryColor }}> — ₹{selectedPlan.pricing.amount}</span>}
                                </div>
                                <button className="isp-change-plan-btn" style={{ color: primaryColor }} onClick={() => setIspStep('select')}>
                                    Change
                                </button>
                            </div>

                            <div className="portal-form">
                                <div className="form-group">
                                    <label>Full Name <span style={{ color: 'red' }}>*</span></label>
                                    <div className="input-with-icon">
                                        <i className="fas fa-user"></i>
                                        <input
                                            type="text"
                                            className="input"
                                            placeholder="Your name"
                                            value={ispInfo.name}
                                            onChange={e => setIspInfo(p => ({ ...p, name: e.target.value }))}
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>
                                        Mobile Number <span style={{ color: 'red' }}>*</span>
                                        {ispVerifiedMobile && <span style={{ color: '#22c55e', fontSize: '0.75rem', marginLeft: 6 }}><i className="fas fa-check-circle"></i> Verified</span>}
                                    </label>
                                    <div className="input-with-icon">
                                        <i className="fas fa-mobile-alt"></i>
                                        <input
                                            type="tel"
                                            className="input"
                                            placeholder="10-digit mobile"
                                            value={ispInfo.phone}
                                            onChange={e => setIspInfo(p => ({ ...p, phone: e.target.value }))}
                                            disabled={!!ispVerifiedMobile}
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Email (optional)</label>
                                    <div className="input-with-icon">
                                        <i className="fas fa-envelope"></i>
                                        <input
                                            type="email"
                                            className="input"
                                            placeholder="email@example.com"
                                            value={ispInfo.email}
                                            onChange={e => setIspInfo(p => ({ ...p, email: e.target.value }))}
                                        />
                                    </div>
                                </div>

                                <button
                                    className="btn btn-primary btn-lg w-full mt-4"
                                    style={btnStyle}
                                    disabled={paymentLoading}
                                    onClick={handleISPPayment}
                                >
                                    {paymentLoading
                                        ? <span className="spinner-border spinner-border-sm"></span>
                                        : <><i className="fas fa-lock mr-2"></i>{selectedPlan.pricing?.amount > 0 ? `Pay ₹${selectedPlan.pricing.amount} Securely` : 'Activate Free Plan'}</>
                                    }
                                </button>

                                <button
                                    className="btn btn-ghost w-full mt-2"
                                    onClick={() => setIspStep('select')}
                                    style={{ color: heading2Color }}
                                >
                                    <i className="fas fa-arrow-left mr-1"></i> Back
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="portal-footer">
                        <p>© {new Date().getFullYear()} {portalData.branchName || 'Network'}. {PORTAL_CREDIT}</p>
                    </div>
                </div>
            </div>
        );
    }

    // 2a-2. "Oakwood ASR" built-in theme preset — fixed design using bundled
    // assets, selected via Templates → Theme Preset (CapitaLand only).
    if (template?.themePreset === 'oakwood-asr') {
        const asrId = formData.capitalandId || '';
        const asrSecret = formData.capitalandSecret || '';
        const asrIsEmail = asrId.includes('@');
        // Access-code fallback reuses the existing 'visitor'/voucher auth path —
        // only offered when the branch/template actually has voucher enabled.
        const asrCodeMode = userType === 'visitor';
        const asrVoucherAvailable = portalData.loginMethods?.includes('voucher');
        // This theme started as an Ascott/Oakwood-only preset with hardcoded
        // photo/header/footer assets. Any property can now use the same
        // layout — hero photo, header banner and footer strip come from the
        // template's own Design uploads (Templates → Background Image / Logo
        // / Footer Strip) when set, falling back to the bundled Ascott/Oakwood
        // assets only for branches that are actually CapitaLand-branded (so
        // the existing Oakwood property keeps its exact original look
        // without needing to re-upload anything).
        const isAscottBranded = portalData?.pmsProvider === 'capitaland';
        const heroImg = design.backgroundImage?.url || (isAscottBranded ? OAKWOOD_HERO : null);
        const headerImg = design.logo?.url || (isAscottBranded ? OAKWOOD_HEADER : null);
        const footerBrandsImg = design.footerStrip?.url || (isAscottBranded ? OAKWOOD_BRANDS : null);
        const t = ASR_I18N[asrLang] || ASR_I18N.en;
        const currentLangLabel = ASR_LANGUAGES.find(l => l.code === asrLang)?.label || 'English';

        return (
            <div className="asr-root">
                <style>{OAKWOOD_ASR_CSS}</style>

                <section className="asr-top">
                    {heroImg && <div className="asr-hero-bg" style={{ backgroundImage: `url(${heroImg})` }} />}
                    <div className="asr-hero-shade" />

                    <div className="asr-lang-wrap">
                        <button
                            type="button"
                            className="asr-lang-btn"
                            onClick={() => setShowAsrLangMenu(s => !s)}
                            aria-haspopup="listbox"
                            aria-expanded={showAsrLangMenu}
                        >
                            {currentLangLabel.toUpperCase()} <span className="caret">▾</span>
                        </button>
                        {showAsrLangMenu && (
                            <div className="asr-lang-menu" role="listbox">
                                {ASR_LANGUAGES.map(l => (
                                    <button
                                        key={l.code}
                                        type="button"
                                        role="option"
                                        aria-selected={l.code === asrLang}
                                        className={`asr-lang-option${l.code === asrLang ? ' active' : ''}`}
                                        onClick={() => { setAsrLang(l.code); setShowAsrLangMenu(false); }}
                                    >
                                        {l.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="asr-grid">
                        <div className="asr-left">
                            <h1>{design.heading1?.text || `${t.welcomeTo}\n${portalData.branchName || 'Network'}`.toUpperCase()}</h1>
                        </div>

                        <div className="asr-right">
                            {design.headerTitle ? (
                                <div className="asr-header-fallback">{design.headerTitle}</div>
                            ) : headerImg ? (
                                <img className="asr-header-img" src={headerImg} alt={portalData.branchName || 'Network'} />
                            ) : (
                                <div className="asr-header-fallback">{portalData.branchName || 'WiFi'}</div>
                            )}

                            <div className="asr-panel">
                                {loginError && !asrPmsDown && (
                                    <div className="asr-alert" role="alert">{loginError}</div>
                                )}
                                {asrPmsDown && !asrCodeMode && asrVoucherAvailable && (
                                    <div className="asr-alert">
                                        {t.pmsDown}{' '}
                                        <button
                                            type="button"
                                            className="asr-or-link"
                                            style={{ textDecoration: 'underline' }}
                                            onClick={() => { setAsrPmsDown(false); setUserType('visitor'); }}
                                        >
                                            {t.useCodeInstead}
                                        </button>{t.contactReception}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} noValidate>
                                    <h2 className="asr-form-title">
                                        {asrCodeMode ? t.connectWithCode : (design.heading2?.text || t.connectInternet)}
                                    </h2>
                                    <p className="asr-form-lead">
                                        {asrCodeMode ? t.enterCodeLead : t.signInLead}
                                    </p>

                                    {asrCodeMode ? (
                                        <input
                                            id="asr-code"
                                            className="asr-input"
                                            type="text"
                                            name="voucherCode"
                                            value={formData.voucherCode || ''}
                                            onChange={handleInputChange}
                                            placeholder={t.codePlaceholder}
                                            autoComplete="off"
                                            required
                                        />
                                    ) : (
                                        <>
                                            <select
                                                className="asr-select"
                                                value={odxLoginType}
                                                onChange={e => setOdxLoginType(e.target.value)}
                                            >
                                                <option value="room">Room Guest</option>
                                                <option value="asr">ASR Member</option>
                                            </select>
                                            {odxLoginType === 'room' ? (
                                                <>
                                                    <label className="asr-label" htmlFor="asr-room">Room Number</label>
                                                    <input
                                                        id="asr-room"
                                                        className="asr-input"
                                                        type="text"
                                                        name="roomNumber"
                                                        value={formData.roomNumber || ''}
                                                        onChange={handleInputChange}
                                                        placeholder="Room Number"
                                                        required
                                                    />
                                                    <label className="asr-label" htmlFor="asr-last">Last Name</label>
                                                    <input
                                                        id="asr-last"
                                                        className="asr-input"
                                                        type="text"
                                                        name="lastName"
                                                        value={formData.lastName || ''}
                                                        onChange={handleInputChange}
                                                        placeholder="Guest Last Name"
                                                        required
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <label className="asr-label" htmlFor="asr-email">ASR Member Email</label>
                                                    <input
                                                        id="asr-email"
                                                        className="asr-input"
                                                        type="email"
                                                        name="asrEmail"
                                                        value={formData.asrEmail || ''}
                                                        onChange={handleInputChange}
                                                        placeholder="ASR Member Email"
                                                        autoComplete="username"
                                                        required
                                                    />
                                                    <label className="asr-label" htmlFor="asr-pw">ASR Member Password</label>
                                                    <div className="asr-pw">
                                                        <input
                                                            id="asr-pw"
                                                            className="asr-input"
                                                            type={showCapitalandPw ? 'text' : 'password'}
                                                            name="asrPassword"
                                                            value={formData.asrPassword || ''}
                                                            onChange={handleInputChange}
                                                            placeholder="ASR Member Password"
                                                            autoComplete="current-password"
                                                            required
                                                        />
                                                        <button
                                                            type="button"
                                                            className="asr-pw-toggle"
                                                            onClick={() => setShowCapitalandPw(s => !s)}
                                                            aria-label={showCapitalandPw ? t.hidePw : t.showPw}
                                                        >
                                                            {showCapitalandPw ? t.hidePw : t.showPw}
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}

                                    <div className="asr-row">
                                        <label className="asr-check">
                                            <input
                                                type="checkbox"
                                                name="agreedToTerms"
                                                checked={!!formData.agreedToTerms}
                                                onChange={handleInputChange}
                                            />
                                            {t.agreeToThe}{' '}
                                            <a
                                                href="#"
                                                className="asr-link"
                                                onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                                            >
                                                {t.termsConditions}
                                            </a>

                                        </label>
                                    </div>

                                    <button className="asr-btn asr-btn-primary" type="submit" disabled={loading || !formData.agreedToTerms}>
                                        {loading ? t.pleaseWait : t.continueBtn}
                                    </button>

                                    <p className="asr-join">
                                        {asrCodeMode ? (
                                            <button
                                                type="button"
                                                className="asr-or-link"
                                                onClick={() => setUserType('guest')}
                                            >
                                                {t.orSignInRoom}
                                            </button>
                                        ) : (
                                            asrVoucherAvailable && (
                                                <button
                                                    type="button"
                                                    className="asr-or-link"
                                                    onClick={() => setUserType('visitor')}
                                                >
                                                    {t.orConnectCode}
                                                </button>
                                            )
                                        )}
                                    </p>
                                </form>
                            </div>
                        </div>
                    </div>
                </section>

                <footer className="asr-footer">
                    {(footerBrandsImg || isAscottBranded || design.footerAboutText) && (
                        <div className="asr-footer-inner">
                            {footerBrandsImg && <img className="asr-brands" src={footerBrandsImg} alt={`${portalData.branchName || 'Network'} brands`} />}
                            <div className="asr-about">
                                {isAscottBranded ? (<>
                                    <p>
                                        A trusted hospitality company, Ascott&rsquo;s presence extends across Asia Pacific,
                                        Central Asia, Europe, the Middle East, Africa, and the USA. Its diversified
                                        accommodation offerings span serviced residences, coliving properties and hotels.
                                    </p>
                                    <p>
                                        Ascott&rsquo;s award-winning brands include Ascott, Citadines, lyf, Oakwood, Quest,
                                        Somerset, The Crest Collection, The Unlimited Collection, Preference, Fox, Harris,
                                        POP!, Vertu and Yello. Through Ascott Star Rewards (ASR), Ascott&rsquo;s loyalty
                                        programme, members enjoy exclusive privileges and offers at participating properties.
                                    </p>
                                </>) : design.footerAboutText ? (
                                    design.footerAboutText.split('\n\n').map((p, i) => <p key={i}>{p}</p>)
                                ) : null}
                            </div>
                        </div>
                    )}
                    <div className="asr-copyright">
                        © {new Date().getFullYear()} {portalData.branchName || 'Network'}. {PORTAL_CREDIT}
                    </div>
                </footer>

                {/* CapitaLand ODX: live status popup only applies for actual CapitaLand branches */}
                {loading && userType === 'guest' && isAscottBranded && (
                    <div className="pms-status-overlay">
                        <strong>Checking WiFi Login</strong>
                        <div className="pms-status-row">
                            <span>Login Type</span>
                            <span>{odxLoginType === 'asr' ? 'ASR Member Login' : 'Room Number Login'}</span>
                        </div>
                        <div className="pms-status-row">
                            <span>Status</span>
                            <span>Contacting CapitaLand…</span>
                        </div>
                    </div>
                )}

                {/* ODX positive result: full-screen branded success overlay with hotel image */}
                {pmsConnected && (
                    <div className="asr-success-overlay">
                        <div className="asr-success-card">
                            {headerImg && (
                                <img
                                    src={headerImg}
                                    alt={portalData.branchName || 'Hotel'}
                                    className="asr-success-hotel-img"
                                />
                            )}
                            <div className="asr-success-check">✓</div>
                            <h2>Login Successful!</h2>
                            <p className="asr-success-type">{pmsConnected.loginType}</p>
                            <p>You're connected. Enjoy your stay!</p>
                            <div className="asr-success-spin" />
                            <p className="asr-success-redir">Connecting to internet…</p>
                        </div>
                    </div>
                )}

                {termsModal}
            </div>
        );
    }

    // 2b. Main Portal State (non-ISP)
    // Hero layout: when a background photo is set, match the reference design —
    // big heading overlaid on the left of the photo, login card pinned to the right
    // (instead of the default centered card on a plain gradient background).
    // Only activates when a template has a backgroundImage set — other templates
    // (no photo) are completely unaffected and keep the original centered layout.
    const isHeroLayout = !!backgroundImageUrl;

    return (
        <div className={`portal-container${isHeroLayout ? ' hero-layout' : ''}`} style={containerStyle}>
            {isHeroLayout && (
                <div className="hero-heading-overlay">
                    <h1 style={{ color: heading1Color === '#111827' ? '#ffffff' : heading1Color }}>
                        {design.heading1?.text || portalData.branchName || 'Welcome'}
                    </h1>
                </div>
            )}
            <div
                className={`portal-card card animate-fade-in-up${isHeroLayout ? ' hero-card' : ''}`}
                style={isHeroLayout ? { backgroundColor: heading2Color !== '#6b7280' ? heading2Color : '#0f4c4c' } : undefined}
            >

                {/* Header */}
                <div className="portal-header">
                    {partnerBannerUrl && (
                        <img src={partnerBannerUrl} alt="Partner banner" className="partner-banner" />
                    )}
                    {!isHeroLayout && (logoUrl ? (
                        <img src={logoUrl} alt="Logo" className="brand-logo" />
                    ) : <DefaultLogo />)}

                    {!isHeroLayout && (
                        <h1 style={{ color: heading1Color }}>
                            {design.heading1?.text || portalData.branchName || 'Welcome'}
                        </h1>
                    )}
                    <p className={isHeroLayout ? 'hero-card-heading' : 'welcome-msg'} style={isHeroLayout ? undefined : { color: heading2Color }}>
                        {design.heading2?.text || portalData.welcomeMessage || 'Sign in to access the network'}
                    </p>
                </div>

                {loginError && (
                    <div className="login-error-alert" role="alert">
                        <i className="fas fa-exclamation-circle"></i> {loginError}
                    </div>
                )}

                {/* Login Methods (Tabs) — order/labels driven by
                    Branch.loginPolicy.methodOrder/methodLabels when the admin has
                    set them; otherwise falls back to this same fixed order/labels
                    as before, so branches that never configured this are unaffected. */}
                <div className="login-tabs">
                    {(() => {
                        // Registry-driven: a method only becomes a tab if it's both
                        // enabled on the branch (portalData.loginMethods, filtered
                        // server-side against SUPPORTED_LOGIN_METHODS) AND has a
                        // renderer registered in AUTH_METHOD_REGISTRY. An enabled
                        // method with no registered renderer is skipped rather than
                        // producing a broken tab — see authRegistry.js.
                        const availableTabs = (portalData.loginMethods || [])
                            .filter(key => key !== 'voucher' || portalData.voucherMode !== 'separate')
                            .map(key => {
                                const entry = AUTH_METHOD_REGISTRY[key];
                                if (!entry) return null;
                                return {
                                    key,
                                    userType: entry.userType,
                                    icon: typeof entry.icon === 'function' ? entry.icon(portalData) : entry.icon,
                                    label: typeof entry.label === 'function' ? entry.label(portalData) : entry.label,
                                };
                            })
                            .filter(Boolean);

                        // Apply admin-configured order: listed methods first (in that
                        // order), anything else keeps its existing relative position.
                        const order = portalData.methodOrder;
                        const sortedTabs = order
                            ? [...availableTabs].sort((a, b) => {
                                const ia = order.indexOf(a.key), ib = order.indexOf(b.key);
                                if (ia === -1 && ib === -1) return 0;
                                if (ia === -1) return 1;
                                if (ib === -1) return -1;
                                return ia - ib;
                            })
                            : availableTabs;

                        return sortedTabs.map(tab => (
                            <button
                                key={tab.key}
                                className={`tab-btn ${userType === tab.userType ? 'active' : ''}`}
                                onClick={() => setUserType(tab.userType)}
                                style={userType === tab.userType ? { borderColor: primaryColor, color: primaryColor } : {}}
                            >
                                <i className={`fas ${tab.icon}`}></i> {portalData.methodLabels?.[tab.key] || tab.label}
                            </button>
                        ));
                    })()}
                    {/* User Registration / Free Access / Form — always last, not part of loginPolicy.methods */}
                    {template?.fields?.length > 0 && (
                        <button
                            className={`tab-btn ${userType === 'form' ? 'active' : ''}`}
                            onClick={() => setUserType('form')}
                            style={userType === 'form' ? { borderColor: primaryColor, color: primaryColor } : {}}
                        >
                            <i className="fas fa-user-edit"></i> Register
                        </button>
                    )}
                </div>

                {/* Form Body */}
                <form className="portal-form" onSubmit={handleSubmit}>

                    {/* --- REGISTRY-DRIVEN AUTH METHOD FIELDS ---
                        A method can only render here if it's both registered in
                        AUTH_METHOD_REGISTRY AND currently enabled in
                        portalData.loginMethods (plus voucher's separate-page
                        guard) — the same two conditions that gate its tab above,
                        by construction. See authRegistry.js. */}
                    {(() => {
                        const activeKey = Object.keys(AUTH_METHOD_REGISTRY).find(key =>
                            AUTH_METHOD_REGISTRY[key].userType === userType &&
                            portalData.loginMethods?.includes(key) &&
                            !(key === 'voucher' && portalData.voucherMode === 'separate')
                        );
                        switch (activeKey) {
                            case 'voucher':
                                return (
                                    <VoucherLoginFields
                                        formData={formData}
                                        handleInputChange={handleInputChange}
                                    />
                                );
                            case 'pms':
                                return (
                                    <PMSLoginFields
                                        portalData={portalData}
                                        formData={formData}
                                        handleInputChange={handleInputChange}
                                        odxLoginType={odxLoginType}
                                        setOdxLoginType={setOdxLoginType}
                                        showGenericPw={showGenericPw}
                                        setShowGenericPw={setShowGenericPw}
                                        primaryColor={primaryColor}
                                    />
                                );
                            case 'otp':
                                return (
                                    <OTPLoginFields
                                        portalData={portalData}
                                        formData={formData}
                                        handleInputChange={handleInputChange}
                                        otpMethod={otpMethod}
                                        setOtpMethod={setOtpMethod}
                                        otpSent={otpSent}
                                        otpTimer={otpTimer}
                                        handleSendOTP={handleSendOTP}
                                        loading={loading}
                                        primaryColor={primaryColor}
                                        btnStyle={btnStyle}
                                        pendingHint={!formData.mobile && pendingCode ? pendingCode.mobileHint : null}
                                    />
                                );
                            case 'resident':
                                return (
                                    <ResidentLoginFields
                                        formData={formData}
                                        handleInputChange={handleInputChange}
                                    />
                                );
                            case 'staff':
                                return (
                                    <StaffLoginFields
                                        formData={formData}
                                        handleInputChange={handleInputChange}
                                    />
                                );
                            default:
                                return null;
                        }
                    })()}

                    {/* --- DYNAMIC FORM --- */}
                    {userType === 'form' && template?.fields && (
                        <div className="animate-fade-in">
                            {template.fields.map((field) => (
                                <div key={field.name} className="form-group">
                                    <label>
                                        {field.label}
                                        {field.required && <span className="text-red-500 ml-1">*</span>}
                                    </label>
                                    <div className="input-with-icon">
                                        <i className={`fas fa-${getIconForField(field.type, field.name)}`}></i>
                                        <input
                                            type={field.type}
                                            name={field.name}
                                            className="input"
                                            placeholder={field.label}
                                            required={field.required}
                                            value={formData[field.name] || ''}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Terms & Submit */}
                    <div className="terms-container">
                        <label className="checkbox-wrapper">
                            <input
                                type="checkbox"
                                name="agreedToTerms"
                                checked={formData.agreedToTerms}
                                onChange={handleInputChange}
                            />
                            <span className="checkbox-text">
                                I agree to the <a href="#" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }} style={linkStyle}>Terms & Conditions</a>
                            </span>
                        </label>
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary btn-lg w-full mt-4"
                        disabled={loading}
                        style={btnStyle}
                    >
                        {loading
                            ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            : (design.button?.text || 'Connect')
                        }
                    </button>
                </form>

                {portalData.loginMethods?.includes('voucher') && portalData.voucherMode === 'separate' && (
                    <div className="voucher-link-row">
                        <i className="fas fa-ticket-alt"></i>{' '}
                        Have a voucher code?{' '}
                        <a
                            href="#"
                            onClick={(e) => { e.preventDefault(); setUserType('visitor'); setShowVoucherPage(true); }}
                            style={linkStyle}
                        >
                            Click here
                        </a>
                    </div>
                )}

                {footerStripUrl && !isHeroLayout && (
                    <img src={footerStripUrl} alt="Partner brands" className="footer-strip" />
                )}

                <div className="portal-footer">
                    <p>© {new Date().getFullYear()} {portalData.branchName || 'Network'}. Powered by HotWiFi.</p>
                </div>
            </div>

            {/* Hero layout: brand-strip lives in its own white section below the
                photo, not inside the colored card — matches the reference design */}
            {isHeroLayout && footerStripUrl && (
                <div className="hero-footer-section">
                    <img src={footerStripUrl} alt="Partner brands" />
                </div>
            )}

            {/* CapitaLand ODX: live status while contacting the API */}
            {loading && userType === 'guest' && portalData?.pmsProvider === 'capitaland' && (
                <div className="pms-status-overlay">
                    <strong>Checking WiFi Login</strong>
                    <div className="pms-status-row">
                        <span>Login Type</span>
                        <span>{odxLoginType === 'asr' ? 'ASR Member Login' : 'Room Number Login'}</span>
                    </div>
                    <div className="pms-status-row">
                        <span>Status</span>
                        <span>Contacting CapitaLand...</span>
                    </div>
                </div>
            )}

            {/* Manual Approval Gate — waiting screen */}
            {approvalState && (
                <div className="pms-result-backdrop">
                    <div className="pms-result-box" style={{ textAlign: 'center', padding: '2rem 1.5rem' }}>
                        {approvalState.status === 'pending' && (
                            <>
                                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔐</div>
                                <p style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.5rem' }}>Waiting for Admin Approval</p>
                                <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: '1.25rem' }}>
                                    An approval request has been sent to the administrator.<br />
                                    Please wait while they review your access.
                                </p>
                                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: approvalState.secondsLeft <= 10 ? '#ef4444' : '#7c3aed' }}>
                                    {approvalState.secondsLeft}s
                                </div>
                                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.5rem' }}>Request expires in {approvalState.secondsLeft} seconds</p>
                            </>
                        )}
                        {approvalState.status === 'approved' && (
                            <>
                                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
                                <p style={{ fontWeight: 700, fontSize: '1.05rem' }}>Access Approved!</p>
                                <p style={{ color: '#16a34a', fontSize: '0.88rem' }}>Connecting you to the internet…</p>
                            </>
                        )}
                        {approvalState.status === 'rejected' && (
                            <>
                                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>❌</div>
                                <p style={{ fontWeight: 700, fontSize: '1.05rem' }}>Access Denied</p>
                                <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: '1rem' }}>The administrator rejected your access request.</p>
                                <button className="btn btn-secondary" onClick={() => setApprovalState(null)} style={{ fontSize: '0.85rem' }}>Try Again</button>
                            </>
                        )}
                        {approvalState.status === 'expired' && (
                            <>
                                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⏱️</div>
                                <p style={{ fontWeight: 700, fontSize: '1.05rem' }}>Request Timed Out</p>
                                <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: '1rem' }}>No response from admin within 60 seconds.</p>
                                <button className="btn btn-secondary" onClick={() => setApprovalState(null)} style={{ fontSize: '0.85rem' }}>Try Again</button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* CapitaLand ODX: post-login confirmation popup */}
            {pmsResult && (
                <div className="pms-result-backdrop">
                    <div className="pms-result-box">
                        <p><strong>Login Type :</strong> {pmsResult.loginType}</p>
                        <p><strong>Allow WiFi :</strong> {pmsResult.allowWifi ? 'YES' : 'NO'}</p>
                        <p><strong>API Message :</strong> {pmsResult.apiMessage}</p>
                        <p className="pms-result-friendly">Member WiFi is active now. Have a nice day!</p>
                        <button
                            type="button"
                            className="btn btn-primary w-full"
                            style={btnStyle}
                            onClick={() => {
                                const loginType = pmsResult.loginType;
                                setPmsResult(null);
                                setPmsConnected({ loginType });
                                setTimeout(() => {
                                    if (pendingRedirectRef.current) pendingRedirectRef.current();
                                }, 2500);
                            }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* CapitaLand ODX: "you're connected" success screen, shown briefly before the actual redirect */}
            {pmsConnected && (
                <div className="pms-result-backdrop">
                    <div className="pms-result-box pms-connected-box">
                        <div className="pms-connected-icon"><i className="fas fa-check-circle"></i></div>
                        <h3>Login Successful!</h3>
                        {/* Other PMSs (HOTLA, ...): the guest is verified, but internet only
                            starts once the redirect below signs them in at the WiFi gateway. */}
                        {portalData?.pmsProvider === 'capitaland'
                            ? <p>You're connected to the internet via <strong>CapitaLand ODX</strong>.</p>
                            : <p>Your stay is verified. Connecting you to the internet…</p>}
                        <p className="pms-status-row" style={{ justifyContent: 'center', gap: 6 }}>
                            <span>{pmsConnected.loginType}</span>
                        </p>
                        <p className="pms-result-friendly">
                            {portalData?.pmsProvider === 'capitaland' ? 'Internet is now active. Enjoy your stay!' : 'Enjoy your stay!'}
                        </p>
                        <div className="pms-connected-spinner"></div>
                        <p className="pms-redirecting-text">Redirecting...</p>
                    </div>
                </div>
            )}
            {termsModal}
        </div>
    );
};

// Helper utility for icons
const getIconForField = (type, name) => {
    if (name.includes('email')) return 'envelope';
    if (name.includes('phone') || name.includes('mobile')) return 'phone';
    if (name.includes('user') || name.includes('name')) return 'user';
    if (name.includes('company')) return 'building';
    if (name.includes('address')) return 'map-marker-alt';
    return 'pen';
};

export default Landing;